import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./CareerCard.css";
import { Modal, Button } from "react-bootstrap";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown as EducationDropDown } from "primereact/dropdown";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import TooltipIcon from "../common/TooltipIcon";
import { getTooltipById } from "../../config/tooltipConfig";
import { useSelector, useDispatch } from "react-redux";
import type { AppDispatch, RootState } from "../../app/store";
import { fetchDropdowns } from "../CareerCard/DropdownSlice";
import { fetchEmpDetails, resetEmpDetails } from "./CareerCardDetailsSlice";
import {
  fetchEmpOracleDetails,
  resetEmpOracleDetails,
} from "../CareerCard/EmpOracleDetailsSlice";
import { addCareerCard, resetaddCareerCard } from "./AddCareerCardSlice";
import {
  addCareerCardOtherDetails,
  resetaddCareerCardOtherDetails,
} from "./AddCareerCardOtherDetailsSlice";
import { Dropdown } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  importFromExcelAPICall,
  resetImportFromExcel,
} from "./ImportFromExcelSlice";
import { importFromExcelChunkedAPICall } from "./ImportFromExcelChunkedSlice";
import { uploadFileChunked } from "./GenericChunkUploadSlice";
import useFormValidation, { 
  VALIDATION_ERROR_MESSAGE,
  FIELD_CONFIG
} from "../../hooks/useFormValidation";
import { scanSavedDataForBlockedChars, detectBlockedCharacters } from "../../utils/inputValidation";
import {
  MAX_LANGUAGES_ALLOWED,
  LANGUAGE_LIMIT_WARNING,
  LANGUAGE_IMPORT_ERROR,
} from "../../constants/dropdown";

import { exportCareercardToExcel } from "./exportCareercardToExcel";
import jsPDF from "jspdf";
import CareerCardHtml from "./CareerCardPdfTemplate";
import html2canvas from "html2canvas";
import { sendEmail } from "../../app/utils/email";

interface CareerCardProps {
  empId: string;
  isView: boolean;
  isAdminView?: boolean;
  username?: string;
  onSuccess?: (empId: string, data?: any) => void;
  onError?: (empId: string, error?: string) => void;
  onLoading?: (isLoading: boolean) => void;
}

// Define interfaces for type safety
interface SectionConfig {
  id: string;
  title: string;
  icon: string;
  description: string;
  defaultExpanded: boolean;
  tooltip?: string;
}

// Education level mapping utility
interface EducationMapping {
  level: string;
  label: string;
  keywords: string[];
}

const EDUCATION_MAPPINGS: EducationMapping[] = [
  {
    level: "doctorate",
    label: "Doctorate",
    keywords: [
      "phd",
      "ph.d",
      "doctorate",
      "doctoral",
      "d.phil",
      "dphil",
      "doctor of",
    ],
  },
  {
    level: "masters",
    label: "Master's Degree",
    keywords: [
      "mba",
      "ms",
      "m.s",
      "ma",
      "m.a",
      "msc",
      "m.sc",
      "master",
      "masters",
      "post graduate",
      "postgraduate",
      "mtech",
      "m.tech",
      "mca",
      "m.ca",
    ],
  },
  {
    level: "bachelors",
    label: "Bachelor's Degree",
    keywords: [
      "bachelor",
      "bachelors",
      "bs",
      "b.s",
      "ba",
      "b.a",
      "bsc",
      "b.sc",
      "btech",
      "b.tech",
      "be",
      "b.e",
      "bcom",
      "b.com",
      "bba",
      "b.ba",
      "undergraduate",
      "honors degree",
      "bmba",
    ],
  },
  {
    level: "associates",
    label: "Associate's Degree",
    keywords: [
      "associate",
      "associates",
      "aa",
      "a.a",
      "as",
      "a.s",
      "aas",
      "a.a.s",
    ],
  },
  {
    level: "technical",
    label: "Technical College",
    keywords: ["technical", "diploma", "certificate", "vocational", "trade"],
  },
  {
    level: "some_college",
    label: "Some College",
    keywords: ["some college", "college courses", "partial", "incomplete"],
  },
  {
    level: "high_school",
    label: "High School Diploma or GED",
    keywords: ["high school", "ged", "secondary", "matriculation"],
  },
  {
    level: "less_than_high_school",
    label: "Less than High School Diploma or GED",
    keywords: ["elementary", "primary", "less than"],
  },
];

// Education dropdown options
const EDUCATION_OPTIONS = [
  { label: "Associate's Degree", value: "associates" },
  { label: "Bachelor's Degree", value: "bachelors" },
  { label: "Master's Degree", value: "masters" },
  { label: "Doctorate", value: "doctorate" },
  { label: "High School Diploma or GED", value: "high_school" },
  {
    label: "Less than High School Diploma or GED",
    value: "less_than_high_school",
  },
  { label: "Some College", value: "some_college" },
  { label: "Technical College", value: "technical" },
  { label: "Other", value: "other" },
];

// Utility function to map database education value to category
const mapEducationToCategory = (
  educationValue: string
): { category: string; originalValue: string } => {
  if (!educationValue) {
    return { category: "", originalValue: "" };
  }

  const lowercaseValue = educationValue.toLowerCase().trim();

  for (const mapping of EDUCATION_MAPPINGS) {
    for (const keyword of mapping.keywords) {
      if (lowercaseValue.includes(keyword.toLowerCase())) {
        return { category: mapping.level, originalValue: educationValue };
      }
    }
  }

  // If no match found, categorize as 'other'
  return { category: "other", originalValue: educationValue };
};

// Section configuration for the career card
const CAREER_CARD_SECTIONS: SectionConfig[] = [
  {
    id: "employee-data",
    title: "Employee Data",
    icon: "pi pi-user",
    description: "Personal information and basic details",
    defaultExpanded: true,
  },
  {
    id: "location-mobility",
    title: "Internal Mobility",
    icon: "pi pi-map-marker",
    description: "Location preferences and mobility options",
    defaultExpanded: true,
  },
  {
    id: "role-aspirations",
    title: "Role Readiness",
    icon: "bi bi-bullseye",
    description: "Career goals and role preferences",
    defaultExpanded: true,
    tooltip:
      "Select when you believe you'll be ready for the next role (e.g., Ready Now, 1–2 years, 2–5 years).",
  },
  {
    id: "experience",
    title: "Experience (TTEC and External)",
    icon: "pi pi-briefcase",
    description: "Work history (maximum 4 entries, recent to old)",
    defaultExpanded: true,
    tooltip:
      "Add up to 4 professional experiences including TTEC and external roles",
  },
  {
    id: "leadership-roles",
    title: "Additional TTEC Leadership Roles or Involvement",
    icon: "pi pi-users",
    description: "ERGs and community involvement",
    defaultExpanded: true,
  },
  {
    id: "leadership-culture",
    title: "Leadership Culture (Executive Competencies)",
    icon: "pi pi-star",
    description: "Executive competencies and development areas",
    defaultExpanded: true,
  },
  {
    id: "performance-summary",
    title: "Performance Summary",
    icon: "pi pi-chart-bar",
    description: "Accomplishments, improvements, and goals",
    defaultExpanded: true,
    tooltip:
      "Performance review including achievements, areas for improvement, and future goals",
  },
];

const Toast: React.FC<{
  message: string;
  type: "success" | "error" | "warning";
  onClose: () => void;
  showProgress?: boolean;
  duration?: number;
}> = ({ message, type, onClose, showProgress = false, duration = 5000 }) => {
  const [progress, setProgress] = React.useState(100);

  React.useEffect(() => {
    if (!showProgress) return;

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [showProgress, duration]);

  const bgColor = {
    success: "#28a745",
    error: "#dc3545",
    warning: "#ffc107",
  }[type];

  const textColor = type === "warning" ? "#000" : "#fff";

  return (
    <div
      style={{
        position: "fixed",
        top: "20px",
        right: "20px",
        backgroundColor: bgColor,
        color: textColor,
        padding: "12px 20px",
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        zIndex: 10000,
        maxWidth: "400px",
        fontSize: "14px",
        display: "flex",
        flexDirection: "column",
        animation: "slideIn 0.3s ease-out",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ flex: 1, marginRight: "10px" }}>{message}</div>
        <button
          onClick={onClose}
          style={{
            background: "transparent",
            border: "none",
            color: textColor,
            fontSize: "18px",
            cursor: "pointer",
            padding: "0",
            lineHeight: "1",
          }}
          aria-label="Close notification"
        >
          <i className="bi bi-x" aria-hidden="true"></i>
        </button>
      </div>
      {showProgress && (
        <div
          style={{
            marginTop: "8px",
            height: "3px",
            backgroundColor: "rgba(255,255,255,0.3)",
            borderRadius: "2px",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              backgroundColor: textColor,
              width: `${progress}%`,
              transition: "width 0.1s linear",
              borderRadius: "2px",
            }}
          />
        </div>
      )}
      <style>
        {`
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        `}
      </style>
    </div>
  );
};

const CareerCard: React.FC<CareerCardProps> = ({
  empId,
  isView,
  isAdminView,
  username,
  onSuccess,
  onError,
  onLoading,
}) => {
  // Utility function to safely display year from date string
  const displayYearFromDate = (dateString: string) => {
    if (!dateString) return "";
    if (dateString === "2999-12-31") return "Present";
    const date = new Date(dateString);
    return date.toString() !== "Invalid Date"
      ? date.getFullYear().toString()
      : dateString;
  };

  // Progress loader state for chunked uploads
  const [uploadProgress, setUploadProgress] = useState({
    show: false,
    current: 0,
    total: 0,
    label: "",
  });

  interface ToastNotification {
    id: number;
    message: string;
    type: "success" | "error" | "warning";
    showProgress?: boolean;
    duration?: number;
  }

  const [data, setData] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [accomplishments, setAccomplishments] = useState("");
  const [redo, setRedo] = useState("");
  const [redoBusiness, setRedoBusiness] = useState("");
  const [goals, setGoals] = useState("");
  const [resourceGroup, setResourceGroup] = useState<number | "">("");
  const [ttecCommunity, setTtecCommunity] = useState<string>("");
  const [strength, setStrength] = useState<number | "">("");
  const [development, setDevelopment] = useState("");

  // Education dropdown states
  const [educationLevel, setEducationLevel] = useState<string>("");
  const [educationOther, setEducationOther] = useState<string>("");
  const [showEducationOther, setShowEducationOther] = useState<boolean>(false);
  const [educationDropdownKey, setEducationDropdownKey] = useState<number>(0);

  // Default to empty string (shows person icon placeholder), will be overridden with base64 image from API if available
  const [imageUrl, setImageUrl] = useState<string>("");
  const [showImageInput, setShowImageInput] = useState(false);
  const [imageWarning, setImageWarning] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const errorContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch<AppDispatch>();
  const dropdowns = useSelector((state: RootState) => state.fetchDropdowns);
  const empDetails = useSelector((state: RootState) => state.empDetails);
  const [emailMe, setEmailMe] = useState(false);
  const [emailManager, setEmailManager] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const empOracleDetails = useSelector(
    (state: RootState) => state.empOracleDetails
  );
  const addCareerCardState = useSelector(
    (state: RootState) => state.addCareerCard
  );
  const addCareerCardStatus = addCareerCardState.status;
  // Access the response data correctly from the state

  const addCareerCardOtherDetailsStatus = useSelector(
    (state: RootState) => state.addCareerCardOtherDetails?.status
  );

  // Store selected values as IDs
  const [languages, setLanguages] = useState<number[]>([]);
  const [locationPreferences, setLocationPreferences] = useState<number[]>([]);
  const [locationOthersComments, setLocationOthersComments] =
    useState<string>("");
  const [showLocationOthersInput, setShowLocationOthersInput] =
    useState<boolean>(false);

  const isLanguageLimitReached = useMemo(
    () => languages.length >= MAX_LANGUAGES_ALLOWED,
    [languages]
  );
  const remainingLanguageSlots = useMemo(
    () => MAX_LANGUAGES_ALLOWED - languages.length,
    [languages]
  );

  // Additional states for new requirements
  const [strengthsDisplayed, setStrengthsDisplayed] = useState<number[]>([]);
  const [areasOfDevelopment, setAreasOfDevelopment] = useState<number[]>([]);
  const currentYear = new Date().getFullYear().toLocaleString();
  // Background PDF generation refs
  const backgroundPdfRef = useRef<HTMLDivElement>(null);
  const [pdfProgress, setPdfProgress] = useState<{
    show: boolean;
    current: number;
    total: number;
  }>({
    show: false,
    current: 0,
    total: 0,
  });

  // Add a form wrapper and required validation for all fields
  const [formErrors, setFormErrors] = useState<any>({});
  const [originalState, setOriginalState] = useState<any>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState<boolean>(false);
  const [showExportSuccess, setShowExportSuccess] = useState<boolean>(false);
  const [unifiedExperiences, setUnifiedExperiences] = useState<any[]>([]);
  const [showDeleteExperienceConfirm, setShowDeleteExperienceConfirm] = useState<boolean>(false);
  const [deleteExperienceIndex, setDeleteExperienceIndex] = useState<number | null>(null);

  // Section expansion state
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(
      CAREER_CARD_SECTIONS.filter((s) => s.defaultExpanded).map((s) => s.id)
    )
  );

  const [expandedViewLists, setExpandedViewLists] = useState<Record<string, boolean>>({});
  const [maxViewItems, setMaxViewItems] = useState(4);

  useEffect(() => {
    const updateMaxItems = () => {
      const w = window.innerWidth;
      setMaxViewItems(w < 576 ? 1 : w < 768 ? 2 : w < 992 ? 3 : 4);
    };
    updateMaxItems();
    window.addEventListener('resize', updateMaxItems);
    return () => window.removeEventListener('resize', updateMaxItems);
  }, []);

  const [toasts, setToasts] = useState<ToastNotification[]>([]);
  const [nextToastId, setNextToastId] = useState(1);
  const initialLoadRef = useRef(false);
  const fetchInProgressRef = useRef(false);
  const lastImportStatusRef = useRef<string>("");
  const preImportDataRef = useRef<any>(null);
  const needsComparisonRef = useRef<boolean>(false);
  const isSaveOperationRef = useRef<boolean>(false);
  const lastSaveStatusRef = useRef<string>(""); // Track last processed save status

  // State to track changed fields after import
  const [changedFields, setChangedFields] = useState<Set<string>>(new Set());
  const [showChangedFieldsIndicator, setShowChangedFieldsIndicator] =
    useState(false);

  // Centralized form validation hook
  const {
    errors: validationErrors,
    validateAndSanitize,
    clearError,
    clearAllErrors,
    hasErrors: hasValidationErrors,
    registerRef,
    scrollToFirstError,
    scrollToField,
    getFieldError,
    getInputClassName,
  } = useFormValidation();

  const [savedDataWarnings, setSavedDataWarnings] = useState<
    Array<{ field: string; displayName: string; blockedChars: string[] }>
  >([]);
  const [showSavedDataWarningBanner, setShowSavedDataWarningBanner] = useState(false);
  const [currentWarningIndex, setCurrentWarningIndex] = useState(0);

  const clearSavedDataWarning = useCallback((fieldName: string) => {
    setSavedDataWarnings(prev => {
      const updated = prev.filter(w => w.field !== fieldName);
      if (updated.length === 0) {
        setShowSavedDataWarningBanner(false);
      }

      setCurrentWarningIndex(idx => Math.min(idx, Math.max(0, updated.length - 1)));
      return updated;
    });
  }, []);

  const [importValidationErrors, setImportValidationErrors] = useState<
    Array<{ field: string; message: string; blockedChars: string[]; errorType: string }>
  >([]);
  const [showImportErrorBanner, setShowImportErrorBanner] = useState(false);
  const [importErrorMessage, setImportErrorMessage] = useState<string>("");

  // Helper function to normalize values for consistent comparison
  const normalizeValue = useCallback((value: any): string => {
    if (value === null || value === undefined) return "";
    if (typeof value === "string") {
      // Handle special characters and normalize whitespace
      return value.trim().replace(/\s+/g, " ");
    }
    if (Array.isArray(value)) {
      // Sort arrays and filter out null/undefined values for consistent comparison
      const cleanArray = value.filter(
        (item) => item !== null && item !== undefined
      );
      return JSON.stringify(cleanArray.sort());
    }
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, type: "success" | "error" | "warning") => {
      // Prevent duplicate toasts with same message within 1 second
      const isDuplicate = toasts.some(
        (toast) =>
          toast.message === message &&
          toast.type === type &&
          Date.now() - toast.id < 1000
      );

      if (isDuplicate) return;

      const newToast: ToastNotification = {
        id: Date.now(), // Use timestamp as ID for better deduplication
        message,
        type,
      };
      setToasts((prev) => [...prev, newToast]);

      setTimeout(() => {
        removeToast(newToast.id);
      }, 5000);
    },
    [removeToast, toasts]
  );

  const showFieldChangeToast = useCallback(
    (message: string, type: "success" | "error" | "warning") => {
      // Check for duplicate field change toasts
      const isDuplicate = toasts.some(
        (toast) =>
          toast.message === message &&
          toast.type === type &&
          toast.showProgress === true &&
          Date.now() - toast.id < 2000
      );

      if (isDuplicate) return;

      const newToast: ToastNotification = {
        id: Date.now(),
        message,
        type,
        showProgress: true,
        duration: 25000,
      };
      setToasts((prev) => [...prev, newToast]);

      setTimeout(() => {
        removeToast(newToast.id);
      }, 25000);
    },
    [removeToast, toasts]
  );

  // Function to capture current data before import
  const capturePreImportData = useCallback(() => {
    if (data) {
      const capturedData = {
        // Basic fields (excluding name field due to update conflicts)
        // name: normalizeValue(data.name), // Excluded - causes update conflicts
        oracle_id: normalizeValue(data.oracle_id),
        email_id: normalizeValue(data.email_id),
        current_location: normalizeValue(data.current_location),
        willingness_to_relocate: normalizeValue(data.willingness_to_relocate),
        next_role_preference: normalizeValue(data.next_role_preference),
        future_career_aspirations: normalizeValue(
          data.future_career_aspirations
        ),
        timing_for_next_role: normalizeValue(data.timing_for_next_role),
        time_in_current_role: normalizeValue(data.time_in_current_role),
        education_degree: normalizeValue(data.education_degree),
        certificates: normalizeValue(data.certificates),

        // Performance fields
        last_year_top_achievement: normalizeValue(accomplishments),
        last_year_personal_development_review: normalizeValue(redo),
        redo_for_previous_business_year: normalizeValue(redoBusiness),
        goal_next_two_quarters: normalizeValue(goals),

        // Arrays (convert to strings for comparison)
        emp_languages: JSON.stringify(data.emp_languages || []),
        EmpLocationPreferences: JSON.stringify(
          data.EmpLocationPreferences || []
        ),
        EmpResourceGroup: JSON.stringify(data.EmpResourceGroup || []),
        EmpTtecCommunities: JSON.stringify(data.EmpTtecCommunities || []),
        EmpLeadershipCulture: JSON.stringify(data.EmpLeadershipCulture || []),
        work_experiences: JSON.stringify(data.work_experiences || []),

        image: normalizeValue(data.image),
      };

      preImportDataRef.current = capturedData;
    }
  }, [data, accomplishments, redo, redoBusiness, goals, normalizeValue]);

  // Function to compare current data with pre-import data
  const compareAndHighlightChanges = useCallback(() => {
    try {
      if (!preImportDataRef.current || !data) {
        return;
      }

      const changes = new Set<string>();
      const preData = preImportDataRef.current;
      const currentData = {
        // Basic fields (excluding name field due to update conflicts)
        // name: data.name || "", // Excluded - causes update conflicts
        oracle_id: data.oracle_id || "",
        email_id: data.email_id || "",
        current_location: data.current_location || "",
        willingness_to_relocate: data.willingness_to_relocate || "",
        next_role_preference: data.next_role_preference || "",
        future_career_aspirations: data.future_career_aspirations || "",
        timing_for_next_role: data.timing_for_next_role || "",
        time_in_current_role: data.time_in_current_role || "",
        education_degree: data.education_degree || "",
        certificates: data.certificates || "",

        // Performance fields
        last_year_top_achievement: accomplishments || "",
        last_year_personal_development_review: redo || "",
        redo_for_previous_business_year: redoBusiness || "",
        goal_next_two_quarters: goals || "",

        // Arrays (convert to strings for comparison)
        emp_languages: JSON.stringify(data.emp_languages || []),
        EmpLocationPreferences: JSON.stringify(
          data.EmpLocationPreferences || []
        ),
        EmpResourceGroup: JSON.stringify(data.EmpResourceGroup || []),
        EmpTtecCommunities: JSON.stringify(data.EmpTtecCommunities || []),
        EmpLeadershipCulture: JSON.stringify(data.EmpLeadershipCulture || []),
        work_experiences: JSON.stringify(data.work_experiences || []),

        image: data.image || "",
      };

      // Compare each field with normalized values
      Object.keys(currentData).forEach((key) => {
        const preValue = normalizeValue(preData[key]);
        const currentValue = normalizeValue(
          currentData[key as keyof typeof currentData]
        );

        if (preValue !== currentValue) {
          // Additional check for meaningful changes (ignore empty string differences)
          const hasPreValue = preValue && preValue.length > 0;
          const hasCurrentValue = currentValue && currentValue.length > 0;

          // Only consider it a change if:
          // 1. Both have values and they're different, OR
          // 2. One has a value and the other doesn't
          if (
            (hasPreValue && hasCurrentValue && preValue !== currentValue) ||
            hasPreValue !== hasCurrentValue
          ) {
            changes.add(key);
          }
        }
      });

      setChangedFields(changes);

      if (changes.size > 0) {
        setShowChangedFieldsIndicator(true);
        showFieldChangeToast(
          `${changes.size} field${
            changes.size > 1 ? "s" : ""
          } updated from Excel import. Changed fields are highlighted.`,
          "success"
        );

        // Auto-hide the highlights after 30 seconds
        setTimeout(() => {
          setShowChangedFieldsIndicator(false);
          setChangedFields(new Set());
        }, 30000);
      } else {
        showToast(
          "Excel file imported successfully, but no field changes were detected.",
          "warning"
        );
      }

      // Clear the pre-import data
      preImportDataRef.current = null;
    } catch (error) {
      // Handle comparison errors gracefully
      showToast(
        "Error comparing data after import. Please try again.",
        "error"
      );
      preImportDataRef.current = null;
      setShowChangedFieldsIndicator(false);
      setChangedFields(new Set());
    }
  }, [
    data,
    accomplishments,
    redo,
    redoBusiness,
    goals,
    showToast,
    showFieldChangeToast,
    normalizeValue,
  ]);

  // Function to check if a field has changed
  const isFieldChanged = useCallback(
    (fieldName: string) => {
      return showChangedFieldsIndicator && changedFields.has(fieldName);
    },
    [showChangedFieldsIndicator, changedFields]
  );

  // Function to get highlight classes for changed fields
  const getFieldHighlightClass = useCallback(
    (fieldName: string) => {
      return isFieldChanged(fieldName)
        ? "border-warning bg-warning bg-opacity-10 border-2"
        : "";
    },
    [isFieldChanged]
  );

  // Education dropdown handlers
  const handleEducationLevelChange = (e: any) => {
    const selectedLevel = e.value;
    setEducationLevel(selectedLevel);

    if (selectedLevel && selectedLevel !== "other") {
      // For predefined categories, show the input for specific degree details
      setShowEducationOther(true);
      // Keep existing education_degree value or set to empty for new selection
      if (!educationOther) {
        setEducationOther(data?.education_degree || "");
      }
    } else if (selectedLevel === "other") {
      // For "other" category, show input and clear any category-specific data
      setShowEducationOther(true);
      setEducationOther("");
    } else {
      // No selection made
      setShowEducationOther(false);
      setEducationOther("");
    }
  };

  const handleEducationOtherChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const otherValue = validateAndSanitize('education_degree', e.target.value, 'education');
    setEducationOther(otherValue);
    // Update the main data object immediately
    setData((prevData: any) => ({
      ...prevData,
      education_degree: otherValue,
    }));
  };

  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({
    current: 0,
    total: 2,
    status: "Starting...",
  });

  // Progress indicator component
  const LoadingProgress = () => (
    <>
      <div className="loading-backdrop"></div>
      <div
        className="loading-modal"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "rgba(255, 255, 255, 0.98)",
          color: "#333333",
          padding: "30px 40px",
          borderRadius: "12px",
          border: "2px solid rgb(244, 247, 249)",
          boxShadow: "0 15px 35px rgba(0, 0, 0, 0.3)",
          zIndex: 9999,
          textAlign: "center",
          fontSize: "16px",
          fontWeight: "500",
          minWidth: "300px",
        }}
      >
        <div className="mb-3">
          <div
            className="spinner-border text-primary mb-2"
            style={{ width: "2.5rem", height: "2.5rem" }}
            role="status"
          >
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="fw-bold">
            {isLoading ? "Loading Employee Profile" : "Saving Employee Profile"}
          </div>
        </div>

        {isLoading && (
          <div className="mb-2">
            <div className="progress" style={{ height: "8px" }}>
              <div
                className="progress-bar bg-primary"
                style={{
                  width: `${
                    (loadingProgress.current / loadingProgress.total) * 100
                  }%`,
                  transition: "width 0.5s ease",
                }}
              ></div>
            </div>
          </div>
        )}

        <div className="small text-muted">
          {isLoading && loadingProgress.status}
        </div>

        <div className="mt-2 small">
          {isLoading &&
            `${loadingProgress.current} of ${loadingProgress.total} completed`}
        </div>
      </div>
    </>
  );

  const handleFetchWithProgress = useCallback(
    (context: string = "Loading") => {
      if (fetchInProgressRef.current) {
        return;
      }

      fetchInProgressRef.current = true;

      // Set loading state immediately
      setIsLoading(true);
      setLoadingProgress({
        current: 0,
        total: 2,
        status: `${context} - Starting...`,
      });

      try {
        // Dispatch the actions to start loading
        dispatch(fetchEmpOracleDetails(empId));
        dispatch(fetchEmpDetails(empId));
      } catch (error) {
        console.error("Error in handleFetchWithProgress:", error);
        fetchInProgressRef.current = false;
        setIsLoading(false);
      }
    },
    [dispatch, empId]
  );

  useEffect(() => {
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;

      dispatch(resetEmpDetails());
      dispatch(resetEmpOracleDetails());
      dispatch(resetaddCareerCard());
      dispatch(resetaddCareerCardOtherDetails());

      setTimeout(() => {
        handleFetchWithProgress("Loading profile");
      }, 50);
    }

    return () => {
      dispatch(resetEmpDetails());
      dispatch(resetEmpOracleDetails());
      dispatch(resetaddCareerCard());
      dispatch(resetaddCareerCardOtherDetails());
    };
  }, [dispatch, handleFetchWithProgress]);

  // Call onLoading callback when loading state changes
  useEffect(() => {
    if (onLoading) {
      onLoading(isLoading);
    }
  }, [isLoading, onLoading]);

  // Map API data to UI
  useEffect(() => {
    if (empDetails.data) {
      // Create a copy of the data with missing fields initialized
      const enhancedData = { ...empDetails.data };

      // Handle missing redo_for_previous_business_year field
      if (!("redo_for_previous_business_year" in enhancedData)) {
        enhancedData.redo_for_previous_business_year = "";
      }

      // Handle missing future_career_aspirations field
      if (
        !("future_career_aspirations" in enhancedData) ||
        enhancedData.future_career_aspirations === null
      ) {
        enhancedData.future_career_aspirations = "";
      }

      // Handle education mapping
      if (enhancedData.education_degree) {
        const educationMapping = mapEducationToCategory(
          enhancedData.education_degree
        );
        setEducationLevel(educationMapping.category || "other");
        setEducationOther(
          educationMapping.originalValue || enhancedData.education_degree
        );
        setShowEducationOther(true);
      } else {
        // Reset education states if no education data
        setEducationLevel("");
        setEducationOther("");
        setShowEducationOther(false);
      }

      // Now set the enhanced data with all fields ensured
      setData(enhancedData);

      // Set individual state values from the enhanced data
      setAccomplishments(enhancedData.last_year_top_achievement || "");
      setRedo(enhancedData.last_year_personal_development_review || "");
      setRedoBusiness(enhancedData.redo_for_previous_business_year || "");
      setGoals(enhancedData.goal_next_two_quarters || "");

      // Set image from API response if available
      if (empDetails.data.image) {
        setImageUrl(empDetails.data.image); // Use the base64 image from API
      }

      // Set TTEC community from EmpTtecCommunities if available
      if (
        Array.isArray(empDetails.data.EmpTtecCommunities) &&
        empDetails.data.EmpTtecCommunities.length > 0
      ) {
        setTtecCommunity(
          empDetails.data.EmpTtecCommunities[0].ttec_communities || ""
        );
      }

      // Map both TTEC and external roles into unified experiences
      const ttecExperiences =
        empDetails.data.work_experiences
          ?.filter((w: any) => w.company_name === "TTEC")
          ?.map((w: any) => ({
            emp_work_experience_uid: w.emp_work_experience_uid,
            isTTEC: true,
            roleTitle: w.previous_position_role,
            companyName: "TTEC",
            startYear: w.starting_date,
            endYear: w.end_date,
            currentRole: w.current_position || false,
            roleDescription: w.short_description || "",
          })) || [];

      const externalExperiences =
        empDetails.data.work_experiences
          ?.filter((w: any) => w.company_name !== "TTEC")
          ?.map((w: any) => ({
            emp_work_experience_uid: w.emp_work_experience_uid,
            isTTEC: false,
            roleTitle: w.previous_position_role,
            companyName: w.company_name,
            startYear: w.starting_date,
            endYear: w.end_date,
            currentRole: w.current_position || false,
            roleDescription: w.short_description || "",
          })) || [];

      // Combine and limit to 4 most recent experiences
      const allExperiences = [...ttecExperiences, ...externalExperiences]
        .sort((a, b) => {
          // Sort by start date descending (most recent first)
          const dateA = new Date(a.startYear || "1900-01-01");
          const dateB = new Date(b.startYear || "1900-01-01");
          return dateB.getTime() - dateA.getTime();
        })
        .slice(0, 4); // Limit to 4 entries

      setUnifiedExperiences(allExperiences);

      // Map strengths displayed and areas of development
      setStrengthsDisplayed(
        empDetails.data.EmpLeadershipCulture?.filter(
          (lc: any) => lc.flag === "STRENGTH"
        ).map(
          (lc: any) =>
            lc.leadership_culture_dropdown_sd?.leadership_culture_dropdown_uid
        ) || []
      );
      setAreasOfDevelopment(
        empDetails.data.EmpLeadershipCulture?.filter(
          (lc: any) => lc.flag === "DEVELOPMENT"
        ).map(
          (lc: any) =>
            lc.leadership_culture_dropdown_sd?.leadership_culture_dropdown_uid
        ) || []
      );
      // Set languages from emp_languages if available
      if (Array.isArray(empDetails.data.emp_languages)) {
        setLanguages(
          empDetails.data.emp_languages.map((l: any) => l.language_dropdown_uid)
        );
      }
      // Set location preferences from EmpLocationPreferences if available
      if (Array.isArray(empDetails.data.EmpLocationPreferences)) {
        const locationIds = empDetails.data.EmpLocationPreferences.map(
          (loc: any) => loc.location_dropdown_uid
        );
        setLocationPreferences(locationIds);

        // Check if "Others" location option exists and has other_preference field
        const othersLocationPreference =
          empDetails.data.EmpLocationPreferences.find(
            (loc: any) => loc.location_dropdown?.location === "Others"
          );

        if (
          othersLocationPreference &&
          othersLocationPreference.other_preference
        ) {
          // Set the other_preference value from the Others location option
          setLocationOthersComments(othersLocationPreference.other_preference);
        } else {
          // Fall back to the location_others_comments field if available (for backward compatibility)
          const empData: any = empDetails.data;
          if (empData.location_others_comments) {
            setLocationOthersComments(empData.location_others_comments);
          }
        }

        // Check if "Others" option is selected to show input field
        const othersLocation = dropdowns?.locations?.find(
          (loc: { location: string }) => loc.location === "Others"
        );

        if (
          othersLocation &&
          locationIds.includes(othersLocation.location_dropdown_uid)
        ) {
          setShowLocationOthersInput(true);
        }
      }
    }
    // Map Oracle details to UI fields
    if (empOracleDetails.data) {
      // Extract from nested response structure
      const oracleMsg = (empOracleDetails.data as any)?.response?.message;

      // Check if there's an image in the Oracle details
      if (oracleMsg?.Employee_Image) {
        setImageUrl(oracleMsg.Employee_Image);
      }

      // console.log("Oracle Data ", empOracleDetails.data);

      setData((prev: any) => ({
        ...prev,
        oracle_id: oracleMsg?.Employee_Oracle || prev?.oracle_id || "",
        email_id: oracleMsg?.Employee_Email || "",
        name:
          `${oracleMsg?.Employee_FirstName || ""} ${
            oracleMsg?.Employee_LastName || ""
          }`.trim() ||
          prev?.name ||
          "",
        manager_oracle: oracleMsg?.Manager_Oracle || "",
        manager_email: oracleMsg?.Manager_Email || "",
        country: oracleMsg?.Country || "",
        location_desc: oracleMsg?.Location_Desc || "",
        employee_status: oracleMsg?.Employee_Status || "",
        timeAtTTEC: (() => {
          // Calculate years and months from hire date to current date
          const hireDate = oracleMsg?.Hire_Date
            ? new Date(oracleMsg.Hire_Date)
            : null;
          if (!hireDate || isNaN(hireDate.getTime())) return "3Years 8Months"; // Default fallback

          const currentDate = new Date();
          const yearDiff = currentDate.getFullYear() - hireDate.getFullYear();
          const monthDiff = currentDate.getMonth() - hireDate.getMonth();

          // Adjust for negative month difference (e.g., hired in December, now it's February)
          if (monthDiff < 0) {
            return `${yearDiff - 1} Year${yearDiff - 1 !== 1 ? "s" : ""} ${
              12 + monthDiff
            } Month${12 + monthDiff !== 1 ? "s" : ""}`;
          } else {
            return `${yearDiff} Year${
              yearDiff !== 1 ? "s" : ""
            } ${monthDiff} Month${monthDiff !== 1 ? "s" : ""}`;
          }
        })(), // Calculate time at TTEC dynamically
        jobLevel: oracleMsg?.Employee_Level || prev?.Employee_Level || "",
        jobTitle: oracleMsg?.Job_Code
          ? oracleMsg.Job_Code.includes(".")
            ? oracleMsg.Job_Code.split(".")[1].trim()
            : oracleMsg.Job_Code
          : prev?.jobTitle || "",
      }));
    }
  }, [empDetails.data, empOracleDetails.data, dropdowns?.locations]);

  useEffect(() => {
    if (empDetails.data) {
      const dataToScan: Record<string, any> = {
        certificates: empDetails.data.certificates,
        education_degree: empDetails.data.education_degree,
        time_in_current_role: (empDetails.data as any).time_in_current_role,
        next_role_preference: empDetails.data.next_role_preference,
        future_career_aspirations: empDetails.data.future_career_aspirations,
        last_year_top_achievement: empDetails.data.last_year_top_achievement,
        last_year_personal_development_review: empDetails.data.last_year_personal_development_review,
        redo_for_previous_business_year: (empDetails.data as any).redo_for_previous_business_year,
        goal_next_two_quarters: empDetails.data.goal_next_two_quarters,
        ttecCommunity: Array.isArray(empDetails.data.EmpTtecCommunities) && empDetails.data.EmpTtecCommunities.length > 0
          ? empDetails.data.EmpTtecCommunities[0].ttec_communities || ''
          : '',
      };

      const warnings = scanSavedDataForBlockedChars(dataToScan);

      if (Array.isArray(empDetails.data.work_experiences)) {
        empDetails.data.work_experiences.forEach((exp: any, idx: number) => {
          const expFields: Record<string, { value: string; displayName: string; ruleType: string }> = {
            [`experience_${idx}_roleTitle`]: { value: exp.previous_position_role || exp.roleTitle || '', displayName: `Work Experience ${idx + 1} — Role Title`, ruleType: 'roleCompany' },
            [`experience_${idx}_companyName`]: { value: exp.company_name || exp.companyName || '', displayName: `Work Experience ${idx + 1} — Company`, ruleType: 'roleCompany' },
            [`experience_${idx}_roleDescription`]: { value: exp.role_description || exp.roleDescription || '', displayName: `Work Experience ${idx + 1} — Description`, ruleType: 'description' },
          };
          for (const [fieldKey, info] of Object.entries(expFields)) {
            if (info.value && info.value.trim()) {
              const blockedChars = detectBlockedCharacters(fieldKey, info.value, info.ruleType as any);
              if (blockedChars.length > 0) {
                warnings.push({ field: fieldKey, displayName: info.displayName, blockedChars });
              }
            }
          }
        });
      }

      setSavedDataWarnings(warnings);
      setShowSavedDataWarningBanner(warnings.length > 0);
    }
  }, [empDetails.data]);

  // Effect to trigger comparison after data mapping is complete
  useEffect(() => {
    // Only trigger comparison if we need to, all data has been mapped, we have pre-import data to compare against, AND we're not in a save operation
    if (
      needsComparisonRef.current &&
      data &&
      accomplishments !== undefined &&
      redo !== undefined &&
      redoBusiness !== undefined &&
      goals !== undefined &&
      preImportDataRef.current !== null &&
      !isSaveOperationRef.current
    ) {
      // Exclude save operations
      // Use a small timeout to ensure all state updates are complete
      setTimeout(() => {
        compareAndHighlightChanges();
        needsComparisonRef.current = false; // Reset the flag
      }, 100);
    }
  }, [
    data,
    accomplishments,
    redo,
    redoBusiness,
    goals,
    compareAndHighlightChanges,
  ]);

  useEffect(() => {
    const hasEmpDetails = empDetails.status === "succeeded" && empDetails.data;
    const hasOracleDetails =
      empOracleDetails.status === "succeeded" && empOracleDetails.data;
    const hasFailure =
      empDetails.status === "failed" || empOracleDetails.status === "failed";
    const isLoading =
      empDetails.status === "loading" || empOracleDetails.status === "loading";

    // Update loading state first
    if (onLoading) {
      onLoading(isLoading);
    }

    if (hasEmpDetails && hasOracleDetails) {
      // Success - we have both data sources
      if (onSuccess) {
        onSuccess(empId, {
          empDetails: empDetails.data,
          oracleDetails: empOracleDetails.data,
        });
      }
    } else if (hasFailure) {
      const errorMessage =
        empDetails.error ||
        empOracleDetails.error ||
        "Failed to load employee data";
      if (onError) {
        onError(empId, errorMessage);
      }
    }
  }, [
    empDetails.status,
    empDetails.data,
    empDetails.error,
    empOracleDetails.status,
    empOracleDetails.data,
    empOracleDetails.error,
    empId,
    onSuccess,
    onError,
    onLoading,
  ]);

  useEffect(() => {
    if (dropdowns.status === "idle") {
      dispatch(fetchDropdowns());
    }
  }, [dispatch, dropdowns.status]);

  // Separate effect to handle dropdown data initialization
  useEffect(() => {
    if (!data || dropdowns.status !== "succeeded") return;

    // Initialize languages from emp_languages array
    if (Array.isArray(data.emp_languages) && data.emp_languages.length > 0) {
      const langIds = data.emp_languages
        .map((l: any) => l.language_dropdown_uid)
        .filter(Boolean);
      if (langIds.length > 0 && languages.length === 0) {
        const limitedLangIds = langIds.slice(0, MAX_LANGUAGES_ALLOWED);
        setLanguages(limitedLangIds);
        if (langIds.length > MAX_LANGUAGES_ALLOWED) {
          showToast(
            LANGUAGE_IMPORT_ERROR(langIds.length),
            "warning"
          );
        }
      }
    }

    // Initialize location preferences from EmpLocationPreferences array
    if (
      Array.isArray(data.EmpLocationPreferences) &&
      data.EmpLocationPreferences.length > 0
    ) {
      const locIds = data.EmpLocationPreferences.map(
        (loc: any) => loc.location_dropdown_uid
      ).filter(Boolean);
      if (locIds.length > 0 && locationPreferences.length === 0) {
        setLocationPreferences(locIds);
      }
    }
  }, [
    data,
    dropdowns.status,
    languages.length,
    locationPreferences.length,
    dropdowns?.locations,
    showToast,
  ]);

  // Effect to scroll to top and focus on errors when they appear
  useEffect(() => {
    // Only show errors if we're in edit mode and there are actual errors
    if (isEditing && Object.keys(formErrors).length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });

      // Focus on the error container after a small delay
      setTimeout(() => {
        if (errorContainerRef.current) {
          errorContainerRef.current.focus();
        }
      }, 500);
    }
  }, [formErrors, isEditing]);

  useEffect(() => {
    // Only process if the status has changed and it's not the same as the last processed status
    if (
      addCareerCardOtherDetailsStatus &&
      addCareerCardOtherDetailsStatus !== lastSaveStatusRef.current
    ) {
      if (addCareerCardOtherDetailsStatus === "succeeded") {
        // setShowSuccessModal(true);
        const newToast: ToastNotification = {
          id: Date.now(), // Use timestamp as unique ID to avoid dependency issues
          message: "Career card successfully submitted!",
          type: "success",
        };
        setToasts((prev) => [...prev, newToast]);

        setTimeout(() => {
          setToasts((prev) => prev.filter((toast) => toast.id !== newToast.id));
        }, 5000);
      }
      // Update the ref to track the last processed status
      lastSaveStatusRef.current = addCareerCardOtherDetailsStatus;
    }
  }, [addCareerCardOtherDetailsStatus]);

  // Handlers for MultiSelects
  const handleLanguagesChange = (e: { value: number[] }) => {
    // Remove duplicates and ensure valid values
    const uniqueValues = Array.from(
      new Set(e.value.filter((v) => v !== null && v !== undefined))
    );

    if (uniqueValues.length > MAX_LANGUAGES_ALLOWED) {
      const limitedValues = uniqueValues.slice(0, MAX_LANGUAGES_ALLOWED);
      setLanguages(limitedValues);

      const selectedNames = dropdowns.languages
        .filter((l: { language: string; language_dropdown_uid: number }) =>
          limitedValues.includes(l.language_dropdown_uid)
        )
        .map((l: { language: string }) => l.language);

      setData((prevData: any) => ({
        ...prevData,
        languages: selectedNames.join(", "),
      }));
      return;
    }

    setLanguages(uniqueValues);

    const selectedNames = dropdowns.languages
      .filter((l: { language: string; language_dropdown_uid: number }) =>
        uniqueValues.includes(l.language_dropdown_uid)
      )
      .map((l: { language: string }) => l.language);

    setData((prevData: any) => ({
      ...prevData,
      languages: selectedNames.join(", "),
    }));
  };

  const handleLocationPreferencesChange = (e: { value: number[] }) => {
    // Remove duplicates and ensure valid values
    const uniqueValues = Array.from(
      new Set(e.value.filter((v) => v !== null && v !== undefined))
    );
    setLocationPreferences(uniqueValues);

    // Fix: ensure 'location' property is accessed correctly
    const selectedNames = dropdowns.locations
      .filter((loc: { location_dropdown_uid: number; location?: string }) =>
        uniqueValues.includes(loc.location_dropdown_uid)
      )
      .map(
        (loc: { location_dropdown_uid: number; location?: string }) =>
          loc.location ?? ""
      );

    setData((prevData: any) => ({
      ...prevData,
      locationPreferences: selectedNames.join(", "),
    }));

    // Check if "Others" option is selected
    const othersLocation = dropdowns.locations.find(
      (loc: { location: string }) => loc.location === "Others"
    );

    if (
      othersLocation &&
      uniqueValues.includes(othersLocation.location_dropdown_uid)
    ) {
      setShowLocationOthersInput(true);
    } else {
      setShowLocationOthersInput(false);
      setLocationOthersComments("");
    }
  };

  const handleUnifiedExperienceChange = (
    index: number,
    field: string,
    value: string | boolean
  ) => {
    setUnifiedExperiences((prev) =>
      prev.map((exp, i) => {
        // Handle isTTEC toggle
        if (field === "isTTEC" && typeof value === "boolean") {
          if (i === index) {
            return {
              ...exp,
              isTTEC: value,
              companyName: value ? "TTEC" : "",
              // If switching to external, uncheck current role and clear special end date
              currentRole: value ? exp.currentRole : false,
              endYear: value ? exp.endYear : exp.currentRole ? "" : exp.endYear,
            };
          }
        }

        // Handle currentRole toggle
        if (field === "currentRole" && typeof value === "boolean") {
          if (i === index) {
            // Only allow current role for TTEC experiences
            if (!exp.isTTEC && value === true) {
              return exp; // Block change for external experiences
            }

            if (value === true) {
              // Mark as current and set end date to 2999-12-31
              return {
                ...exp,
                currentRole: true,
                endYear: "2999-12-31",
              };
            } else {
              // Unmark current and clear end date
              return {
                ...exp,
                currentRole: false,
                endYear: "",
              };
            }
          } else if (value === true) {
            // When marking a new position as current, reset the previous current position's end date
            if (exp.currentRole) {
              return {
                ...exp,
                currentRole: false,
                endYear: "", // Reset end date of previous current position
              };
            }
            return exp;
          }
        }

        // Handle endYear change
        if (field === "endYear" && value && typeof value === "string") {
          if (i === index) {
            return {
              ...exp,
              endYear: value,
              currentRole: value === "2999-12-31",
            };
          }
        }

        // Handle text field changes with validation using FIELD_CONFIG
        if (i === index && typeof value === "string") {
          const fieldId = `experience_${index}_${field}`;
          clearSavedDataWarning(fieldId);
          
          // Check if this field has a validation rule in FIELD_CONFIG
          if (FIELD_CONFIG[field]) {
            const sanitizedValue = validateAndSanitize(fieldId, value, FIELD_CONFIG[field]);
            return { ...exp, [field]: sanitizedValue };
          }
          
          return { ...exp, [field]: value };
        }

        return exp;
      })
    );
  };

  const handleRemoveUnifiedExperience = (index: number) => {
    setDeleteExperienceIndex(index);
    setShowDeleteExperienceConfirm(true);
  };

  const confirmDeleteExperience = () => {
    if (deleteExperienceIndex !== null) {
      setUnifiedExperiences((prev) => prev.filter((_, i) => i !== deleteExperienceIndex));
    }
    setShowDeleteExperienceConfirm(false);
    setDeleteExperienceIndex(null);
  };

  const cancelDeleteExperience = () => {
    setShowDeleteExperienceConfirm(false);
    setDeleteExperienceIndex(null);
  };

  const handleAddUnifiedExperience = () => {
    if (unifiedExperiences.length >= 4) return; // Prevent adding more than 4

    setUnifiedExperiences((prev) => [
      ...prev,
      {
        emp_work_experience_uid: undefined,
        isTTEC: false,
        roleTitle: "",
        companyName: "",
        startYear: "",
        endYear: "",
        currentRole: false,
        roleDescription: "",
      },
    ]);
  };

  const handleLocationOthersCommentsChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setLocationOthersComments(e.target.value);
    setData({ ...data, location_others_comments: e.target.value });
  };

  // API-driven options
  const languageOptions = dropdowns.languages.map(
    (l: { language: string; language_dropdown_uid: number }) => ({
      label: l.language,
      value: l.language_dropdown_uid,
    })
  );
  const locationOptions = dropdowns.locations.map(
    (loc: { location: string; location_dropdown_uid: number }) => ({
      label: loc.location,
      value: loc.location_dropdown_uid,
    })
  );
  const resourceGroupOptions = dropdowns.resourceGroups.map(
    (rg: { resource_group: string; resource_group_dropdown_uid: number }) => ({
      label: rg.resource_group,
      value: rg.resource_group_dropdown_uid,
    })
  );

  const leadershipCultureOptions = dropdowns.leadershipCultures.map(
    (lc: { name: string; leadership_culture_dropdown_uid: number }) => ({
      label: lc.name,
      value: lc.leadership_culture_dropdown_uid,
    })
  );

  const getSelectedItemsLabel = useCallback((
    selectedValues: number[],
    options: Array<{ label: string; value: number }>,
    itemName: string = 'items'
  ): string => {
    if (!selectedValues || selectedValues.length === 0) return `Select ${itemName}...`;
    const firstItem = options.find(opt => opt.value === selectedValues[0]);
    const firstName = firstItem?.label || '';
    if (selectedValues.length === 1) return firstName;
    return `${firstName} +${selectedValues.length - 1} more`;
  }, []);

  const getMaxItemsForField = useCallback((fieldKey: string): number => {
    if (fieldKey === 'locations' || fieldKey === 'locationMobility') {
      return 2;
    }
    return maxViewItems;
  }, [maxViewItems]);

  const renderViewList = useCallback((
    items: Array<any>,
    fieldKey: string,
    getLabel: (item: any) => string
  ): React.ReactNode => {
    if (!items || items.length === 0) return "-";
    
    const isExpanded = expandedViewLists[fieldKey];
    const fieldMaxItems = getMaxItemsForField(fieldKey);
    const visibleItems = isExpanded ? items : items.slice(0, fieldMaxItems);
    const remaining = items.length - fieldMaxItems;

    return (
      <span>
        {visibleItems.map((item, i) => (
          <span key={i}>
            {getLabel(item)}{i < visibleItems.length - 1 ? ', ' : ''}
          </span>
        ))}
        {remaining > 0 && (
          <button
            type="button"
            onClick={() => setExpandedViewLists(prev => ({ ...prev, [fieldKey]: !prev[fieldKey] }))}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              textDecoration: 'underline',
              padding: 0,
              marginLeft: '4px',
              font: 'inherit',
              color: '#0072ff'
            }}
          >
            {isExpanded ? 'Show less' : `+${remaining} more`}
          </button>
        )}
      </span>
    );
  }, [expandedViewLists, getMaxItemsForField]);

  const handleValidatedChange = useCallback((fieldName: string, value: string): string => {
    clearSavedDataWarning(fieldName);
    return validateAndSanitize(fieldName, value);
  }, [validateAndSanitize, clearSavedDataWarning]);

  const handleTextareaChange = useCallback((
    fieldName: string,
    value: string,
    maxLength: number
  ): string => {
    const trimmedValue = value.slice(0, maxLength);
    return handleValidatedChange(fieldName, trimmedValue);
  }, [handleValidatedChange]);
  const getValidatedInputClass = useCallback((fieldName: string, baseClass = ''): string => {
    const hasError = getFieldError(fieldName);
    return `${baseClass}${hasError ? ' is-invalid validation-error' : ''}`.trim();
  }, [getFieldError]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const fieldName = e.target.name;
    let fieldValue = e.target.value;

    fieldValue = handleValidatedChange(fieldName, fieldValue);

    // Make sure we have a valid data object to update
    if (!data) {
      return;
    }

    // Ensure the fields exist in the data object
    const updatedData = { ...data };

    // Update the field value
    updatedData[fieldName] = fieldValue;
    setData(updatedData);
  };

  const handleUpdateClick = () => {
    // Clear any existing form errors before entering edit mode
    setFormErrors({});

    // Store the current state before entering edit mode
    setOriginalState({
      data: JSON.parse(JSON.stringify(data)),
      accomplishments,
      redo,
      redoBusiness,
      goals,
      resourceGroup,
      ttecCommunity,
      strength,
      development,
      imageUrl, // Store the current image URL (base64 string)
      showImageInput: false, // Reset image input view on cancel
      languages: [...languages],
      locationPreferences: [...locationPreferences],
      unifiedExperiences: JSON.parse(JSON.stringify(unifiedExperiences)),
      strengthsDisplayed: [...strengthsDisplayed],
      areasOfDevelopment: [...areasOfDevelopment],
      locationOthersComments,
      showLocationOthersInput,
      // Store education states
      educationLevel,
      educationOther,
      showEducationOther,
      educationDropdownKey,
      // Don't store form errors in original state
    });

    // Force dropdown component re-render by updating key
    setEducationDropdownKey((prev) => prev + 1);

    // Small delay to ensure component is ready
    setTimeout(() => {
      // Ensure dropdown values are properly set for editing
      // Re-sync education dropdown values
      if (data?.education_degree) {
        const educationMapping = mapEducationToCategory(data.education_degree);
        setEducationLevel(educationMapping.category || "other");
        setEducationOther(
          educationMapping.originalValue || data.education_degree
        );
        setShowEducationOther(true);
      }
    }, 100);

    setIsEditing(true);
  };

  // Restore the original state when canceling edit mode
  const handleCancel = () => {
    // Show confirmation dialog
    setShowCancelConfirm(true);
  };

  // Handle cancel confirmation (restore state and exit edit mode)
  const confirmCancel = () => {
    if (originalState) {
      setData(originalState.data);
      setAccomplishments(originalState.accomplishments);
      setRedo(originalState.redo);
      setRedoBusiness(originalState.redoBusiness);
      setGoals(originalState.goals);
      setResourceGroup(originalState.resourceGroup);
      setTtecCommunity(originalState.ttecCommunity);
      setStrength(originalState.strength);
      setDevelopment(originalState.development);
      setImageUrl(originalState.imageUrl); // Restore the original image URL (base64)
      setShowImageInput(false); // Exit image input mode if it was active
      setImageWarning(""); // Clear any image warnings
      setLanguages(originalState.languages);
      setLocationPreferences(originalState.locationPreferences);
      setUnifiedExperiences(originalState.unifiedExperiences);
      setStrengthsDisplayed(originalState.strengthsDisplayed);
      setAreasOfDevelopment(originalState.areasOfDevelopment);
      setLocationOthersComments(originalState.locationOthersComments);
      setShowLocationOthersInput(originalState.showLocationOthersInput);

      // Restore education states
      setEducationLevel(originalState.educationLevel);
      setEducationOther(originalState.educationOther);
      setShowEducationOther(originalState.showEducationOther);
      setEducationDropdownKey(originalState.educationDropdownKey);

      // Clear form errors instead of restoring them
      setFormErrors({});

      // Reset the file input if it exists
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }

    // Force dropdown component re-render after cancel
    setEducationDropdownKey((prev) => prev + 1);

    setIsEditing(false);
    setShowCancelConfirm(false);
  };

  const handleWillingnessChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    // Ensure the value is immediately set in the data object
    const newValue = e.target.value;
    // console.log("Setting willingness_to_relocate:", newValue);
    setData((prevData: any) => {
      if (!prevData) return { willingness_to_relocate: newValue };
      return { ...prevData, willingness_to_relocate: newValue };
    });
  };

  const handleTimingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    // Ensure the value is immediately set in the data object
    const newValue = e.target.value;
    // console.log("Setting timing_for_next_role:", newValue);
    setData((prevData: any) => {
      if (!prevData) return { timing_for_next_role: newValue };
      return { ...prevData, timing_for_next_role: newValue };
    });
  };

  const handleEditImageClick = () => {
    setShowImageInput(true);
    setImageWarning("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageWarning("");
    const file = e.target.files && e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setImageWarning("Please select a valid image file.");
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setImageWarning("Image size should not exceed 2MB.");
        return;
      }

      // Use FileReader to convert the image file to base64 string
      const reader = new FileReader();
      reader.onload = (ev) => {
        // The result is a base64 encoded string
        const base64Image = ev.target?.result as string;

        // Save the base64 string to state (for both display and API submission)
        setImageUrl(base64Image);

        // Also store it in the data object for proper submission
        if (data) {
          setData({
            ...data,
            image: base64Image,
          });
        }

        setShowImageInput(false);
      };

      // This will trigger the onload event when complete
      reader.readAsDataURL(file);
    }
  };

  const handleStrengthsDisplayedChange = (e: { value: number[] }) => {
    setStrengthsDisplayed(e.value);
  };
  const handleAreasOfDevelopmentChange = (e: { value: number[] }) => {
    setAreasOfDevelopment(e.value);
  };

  const validateForm = () => {
    const errors: any = {};

    // Employee Data field validations
    if (data?.time_in_current_role && data.time_in_current_role.trim()) {
      // Check for invalid characters in Time in Current Role
      const timePattern = /^[a-zA-Z0-9\s.-]+$/;
      if (!timePattern.test(data.time_in_current_role)) {
        errors.time_in_current_role = "Time in Current Role can only contain letters, numbers, spaces, periods, and hyphens";
      }
    }

    if (data?.certificates && data.certificates.trim()) {
      // Check for invalid characters in Certifications
      const certPattern = /^[a-zA-Z0-9\s,.()/-]+$/;
      if (!certPattern.test(data.certificates)) {
        errors.certificates = "Certifications can only contain letters, numbers, spaces, commas, periods, hyphens, parentheses, and forward slashes";
      }
    }

    // Professional Experience validations
    const currentPositionCount = unifiedExperiences.filter(
      (exp) => exp.currentRole
    ).length;
    const totalCount = unifiedExperiences.length;

    // Validate based on total count
    if (totalCount > 4) {
      errors.totalExperienceCount = "Maximum 4 experiences allowed";
    }

    // Only one current position allowed
    if (currentPositionCount > 1) {
      errors.currentPositionCount = "Only one current position is allowed";
    }

    // Validate that current position is only for TTEC roles
    unifiedExperiences.forEach((exp, index) => {
      if (exp.currentRole && !exp.isTTEC) {
        errors.externalCurrentRole =
          "Current position can only be marked for TTEC experiences";
      }
    });

    // Validate dates for each experience
    unifiedExperiences.forEach((exp, index) => {
      const currentYear = new Date().getFullYear();
      
      // Validate start year is not in the future
      if (exp.startYear) {
        const startYearNum = /^\d{4}$/.test(exp.startYear) 
          ? parseInt(exp.startYear, 10) 
          : new Date(exp.startYear).getFullYear();
        
        if (!isNaN(startYearNum) && startYearNum > currentYear) {
          errors[`experience${index}StartYear`] = `Experience #${
            index + 1
          }: Start year cannot be in the future`;
        }
      }
      
      // Validate end year is not in the future (skip for current roles)
      if (!exp.currentRole && exp.endYear && exp.endYear !== "2999-12-31") {
        const endYearNum = /^\d{4}$/.test(exp.endYear) 
          ? parseInt(exp.endYear, 10) 
          : new Date(exp.endYear).getFullYear();
        
        if (!isNaN(endYearNum) && endYearNum > currentYear) {
          errors[`experience${index}EndYear`] = `Experience #${
            index + 1
          }: End year cannot be in the future`;
        }
      }
      
      // Skip validation for current roles or if end date is 2999-12-31
      if (!exp.currentRole && exp.endYear !== "2999-12-31") {
        if (exp.startYear && exp.endYear) {
          const startDate = new Date(exp.startYear);
          const endDate = new Date(exp.endYear);

          if (startDate > endDate) {
            errors[`experience${index}Dates`] = `Experience #${
              index + 1
            }: start date cannot be after end date`;
          }
        }
      }
    });

    return errors;
  };

  // Updated buildOtherDetailsPayload to handle add/edit PKIDs and emp_details_uid
  const buildOtherDetailsPayload = (
    mode: "add" | "edit" = "add",
    emp_details_uid_returned?: number
  ) => {
    // Helper to get PKID for each section
    // Deduplicate all multiselect arrays before mapping
    // Languages
    const uniqueLanguages = Array.from(new Set(languages));
    // Location Preferences
    const uniqueLocationPreferences = Array.from(new Set(locationPreferences));
    // Resource Groups - Make sure we have the most current values
    const empResourceGroups = Array.isArray(data?.EmpResourceGroup)
      ? data.EmpResourceGroup
      : [];
    // Don't deduplicate by UID as that loses PKID information, use objects themselves
    const uniqueResourceGroups = empResourceGroups.filter(
      (value: any, index: number, self: any[]) =>
        index ===
        self.findIndex(
          (rg: any) =>
            rg.resource_group_dropdown_uid === value.resource_group_dropdown_uid
        )
    );
    // TTEC Communities
    let communitiesArr: any[] = [];
    if (ttecCommunity) {
       const existingCommunity = Array.isArray(data?.EmpTtecCommunities) && data.EmpTtecCommunities.length > 0
        ? data.EmpTtecCommunities[0]
        : null;
      
      if (existingCommunity && existingCommunity.emp_ttec_communities_uid) {
        communitiesArr = [{
          emp_ttec_communities_uid: existingCommunity.emp_ttec_communities_uid,
          ttec_communities: ttecCommunity
        }];
      } else {
        communitiesArr = [{ ttec_communities: ttecCommunity }];
      }
    }
    // Leadership Culture
    const uniqueStrengthsDisplayed = Array.from(new Set(strengthsDisplayed));
    const uniqueAreasOfDevelopment = Array.from(new Set(areasOfDevelopment));
    return {
      emp_details_uid: emp_details_uid_returned || data?.emp_details_uid || 0,
      location_others_comments: locationOthersComments,
      work_experience: (() => {
        const uniqueExperiences = unifiedExperiences.filter(
          (exp, index, self) =>
            index ===
            self.findIndex(
              (e) =>
                e.roleTitle === exp.roleTitle &&
                e.companyName === exp.companyName &&
                e.startYear === exp.startYear &&
                e.endYear === exp.endYear
            )
        );

        const allRoles = uniqueExperiences.map((exp) => {
          // Convert year strings to date format for API
          const formatYearToDate = (year: string, isEndDate: boolean) => {
            if (!year || year.trim() === "") return "";
            // If it's just a year (1-4 digits), convert to date format
            if (/^\d{1,4}$/.test(year)) {
              const yearNum = parseInt(year);
              if (yearNum >= 1900 && yearNum <= new Date().getFullYear()) {
                return isEndDate ? `${year}-12-31` : `${year}-01-01`;
              }
            }
            // If it's already in date format, return as is
            return year;
          };

          return {
            ...(exp.emp_work_experience_uid && mode === "edit"
              ? { emp_work_experience_uid: exp.emp_work_experience_uid }
              : {}),
            company_name: exp.companyName,
            previous_position_role: exp.roleTitle,
            starting_date: formatYearToDate(exp.startYear, false),
            end_date: formatYearToDate(exp.endYear, true),
            current_position: exp.currentRole || false,
            short_description: exp.roleDescription || "",
          };
        });

        return allRoles.length ? allRoles : undefined;
      })(),
      languages: (() => {
        if (!Array.isArray(uniqueLanguages) || uniqueLanguages.length === 0)
          return undefined;

        // For each language ID, check if it exists in the previous data (for edit mode)
        if (mode === "edit") {
          // Get existing language IDs from API data
          const existingLanguages = Array.isArray(data?.emp_languages)
            ? data.emp_languages.map((l: any) => ({
                id: l.language_dropdown_uid,
                pkid: l.emp_languages_uid,
              }))
            : [];

          return uniqueLanguages.map((langId) => {
            // Find if this language already exists in API data
            const existing = existingLanguages.find(
              (l: { id: number; pkid: number }) => l.id === langId
            );

            // If it exists and has PKID, include the PKID
            if (existing && existing.pkid) {
              return {
                emp_languages_uid: existing.pkid,
                language_dropdown_uid: langId,
              };
            } else {
              // New selection, only send dropdown ID
              return {
                language_dropdown_uid: langId,
              };
            }
          });
        } else {
          // Add mode - only send dropdown IDs
          return uniqueLanguages.map((langId) => ({
            language_dropdown_uid: langId,
          }));
        }
      })(),
      location_preference: (() => {
        if (
          !Array.isArray(uniqueLocationPreferences) ||
          uniqueLocationPreferences.length === 0
        )
          return undefined;

        // For each location ID, check if it exists in the previous data (for edit mode)
        if (mode === "edit") {
          // Get existing location IDs from API data
          const existingLocations = Array.isArray(data?.EmpLocationPreferences)
            ? data.EmpLocationPreferences.map((loc: any) => ({
                id: loc.location_dropdown_uid,
                pkid: loc.emp_location_preference_uid,
              }))
            : [];

          // Get the "Others" location option ID
          const otherLocationId =
            locationOptions.find((opt: any) => opt.label === "Others")?.value ||
            0;

          return uniqueLocationPreferences.map((locId) => {
            // Find if this location already exists in API data
            const existing = existingLocations.find(
              (loc: { id: number; pkid: number }) => loc.id === locId
            );

            // Check if this is the "Others" location option
            const isOthersLocation = locId === otherLocationId;

            // If it exists and has PKID, include the PKID
            if (existing && existing.pkid) {
              return {
                emp_location_preference_uid: existing.pkid,
                location_dropdown_uid: locId,
                // Include other_preference only for the Others option
                ...(isOthersLocation
                  ? { other_preference: locationOthersComments }
                  : { other_preference: null }),
              };
            } else {
              // New selection, send dropdown ID and other_preference if applicable
              return {
                location_dropdown_uid: locId,
                // Include other_preference only for the Others option
                ...(isOthersLocation
                  ? { other_preference: locationOthersComments }
                  : { other_preference: null }),
              };
            }
          });
        } else {
          // Add mode - send dropdown IDs and other_preference for the Others option
          const otherLocationId =
            locationOptions.find((opt: any) => opt.label === "Others")?.value ||
            0;

          return uniqueLocationPreferences.map((locId) => {
            const isOthersLocation = locId === otherLocationId;
            return {
              location_dropdown_uid: locId,
              // Include other_preference for all locations, but only with value for Others
              other_preference: isOthersLocation
                ? locationOthersComments
                : null,
            };
          });
        }
      })(),
      resource_group: (() => {
        if (
          !Array.isArray(uniqueResourceGroups) ||
          uniqueResourceGroups.length === 0
        )
          return undefined;

        return uniqueResourceGroups.map((rg: any) => {
          // For edit mode, include the PKID if it exists
          if (mode === "edit" && rg.emp_resource_group_uid) {
            return {
              emp_resource_group_uid: rg.emp_resource_group_uid,
              resource_group_dropdown_uid: rg.resource_group_dropdown_uid,
            };
          } else {
            // For add mode or new selections in edit mode
            return {
              resource_group_dropdown_uid: rg.resource_group_dropdown_uid,
            };
          }
        });
      })(),
      ttec_communities: (() => {
        if (!Array.isArray(communitiesArr) || communitiesArr.length === 0)
          return undefined;

        // For each community, check if it exists in the previous data (for edit mode)
        if (mode === "edit") {
          // Get existing communities from API data
          const existingCommunities = Array.isArray(data?.EmpTtecCommunities)
            ? data.EmpTtecCommunities.map((tc: any) => ({
                name: tc.ttec_communities,
                pkid: tc.emp_ttec_communities_uid,
              }))
            : [];

          return communitiesArr.map((tc: any) => {
            const communityName = tc.ttec_communities;
            // Find if this community already exists in API data
            const existing = existingCommunities.find(
              (c: { name: string; pkid: number }) => c.name === communityName
            );

            // If it exists and has PKID, include the PKID
            if (existing && existing.pkid) {
              return {
                emp_ttec_communities_uid: existing.pkid,
                ttec_communities: communityName,
              };
            } else {
              // New selection, only send community name
              return {
                ttec_communities: communityName,
              };
            }
          });
        } else {
          // Add mode - only send community names
          return communitiesArr.map((tc: any) => ({
            ttec_communities: tc.ttec_communities,
          }));
        }
      })(),
      leadership_culture: (() => {
        const result: any[] = [];

        // Process strengths and development areas with consistent logic
        const processLeadershipItems = (items: number[], flag: string) => {
          if (!Array.isArray(items) || items.length === 0) return;

          if (mode === "edit") {
            // Get existing leadership culture items from API data
            const existingItems = Array.isArray(data?.EmpLeadershipCulture)
              ? data.EmpLeadershipCulture.map((lc: any) => ({
                  id: lc.leadership_culture_dropdown_sd
                    ?.leadership_culture_dropdown_uid,
                  strength_displayed: lc.strength_displayed,
                  pkid: lc.emp_leadership_culture_uid,
                  flag: lc.flag,
                }))
              : [];

            items.forEach((itemId) => {
              // Find if this item already exists in API data
              const existing = existingItems.find(
                (item: {
                  id: number;
                  strength_displayed: number;
                  pkid: number;
                  flag: string;
                }) => item.id === itemId && item.flag === flag
              );

              // If it exists and has PKID, include the PKID
              if (existing && existing.pkid) {
                result.push({
                  emp_leadership_culture_uid: existing.pkid,
                  flag: flag,
                  strength_displayed: existing.strength_displayed,
                });
              } else {
                // New selection, only send required fields
                result.push({
                  flag: flag,
                  strength_displayed: itemId,
                });
              }
            });
          } else {
            // Add mode - only send required fields without PKIDs
            items.forEach((itemId) => {
              result.push({
                flag: flag,
                strength_displayed: itemId,
              });
            });
          }
        };

        // Process strengths
        processLeadershipItems(uniqueStrengthsDisplayed, "STRENGTH");

        // Process areas of development
        processLeadershipItems(uniqueAreasOfDevelopment, "DEVELOPMENT");

        return result.length ? result : undefined;
      })(),
    };
  };

  useEffect(() => {
    const empOracleStatus = empOracleDetails.status;
    const empDetailsStatus = empDetails.status;
    const isCurrentlyLoading =
      empOracleStatus === "loading" || empDetailsStatus === "loading";

    // Update internal loading state
    setIsLoading(isCurrentlyLoading);

    // Always update progress when there are status changes
    let completed = 0;
    let status = "Loading employee data...";

    if (empOracleStatus === "succeeded" || empOracleStatus === "failed") {
      completed++;
      status = "Oracle details loaded. Loading employee details...";
    }

    if (empDetailsStatus === "succeeded" || empDetailsStatus === "failed") {
      completed++;
      status =
        completed === 2 ? "Loading complete!" : "Employee details loaded...";
    }

    setLoadingProgress({ current: completed, total: 2, status });

    if (!isCurrentlyLoading && completed === 2) {
      // Reset loading progress after a small delay when completely done
      setTimeout(() => {
        setLoadingProgress({ current: 0, total: 2, status: "Ready" });
        fetchInProgressRef.current = false;
      }, 1000);
    }
  }, [empOracleDetails.status, empDetails.status]);

  const handleSaveCareerCard = async (e: any, forceSave = false) => {
    if (e) e.preventDefault();

    // Mark that we're in a save operation to prevent Excel import comparison interference
    isSaveOperationRef.current = true;

    // Reset save status tracking to ensure we only show toast once per save
    lastSaveStatusRef.current = "";

    if (!forceSave && !isAdminView && !emailMe && !emailManager) {
      setShowEmailModal(true);
      isSaveOperationRef.current = false; // Reset flag if returning early
      return;
    }
    if (!isEditing) {
      isSaveOperationRef.current = false; // Reset flag if returning early
      return;
    }

    const willingnessSelect = document.getElementById(
      "willingness_to_relocate"
    ) as HTMLSelectElement;
    const timingSelect = document.getElementById(
      "timing_for_next_role"
    ) as HTMLSelectElement;

    if (willingnessSelect && willingnessSelect.value) {
      setData((prevData: any) => ({
        ...prevData,
        willingness_to_relocate: willingnessSelect.value,
      }));
    }

    if (timingSelect && timingSelect.value) {
      setData((prevData: any) => ({
        ...prevData,
        timing_for_next_role: timingSelect.value,
      }));
    }

    await new Promise((resolve) => setTimeout(resolve, 0));

    const errors = validateForm();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });

      setTimeout(() => {
        if (errorContainerRef.current) {
          errorContainerRef.current.focus();
        }
      }, 500);
      isSaveOperationRef.current = false; // Reset flag on validation error
      return;
    }
    const payload = {
      oracle_id: isAdminView ? data?.oracle_id : "NA",
      email_id: isAdminView ? data?.email_id : "NA",
      image: imageUrl ? imageUrl : null,
      year: currentYear,
      half_year: "H2",
      current_location: data?.location_desc,
      willingness_to_relocate: data?.willingness_to_relocate || "Yes",
      next_role_preference: data?.next_role_preference,
      future_career_aspirations: data?.future_career_aspirations || "", // Ensure this is never null
      timing_for_next_role: data?.timing_for_next_role || "Ready Now",
      last_year_top_achievement: accomplishments,
      last_year_personal_development_review: redo,
      redo_for_previous_business_year: redoBusiness || "", // Ensure this is always included with at least empty string
      goal_next_two_quarters: goals,
      certificates: data?.certificates,
      education_degree: data?.education_degree,
      time_in_current_role: data?.time_in_current_role,
    };

    // HANDLE CHUNKED IMAGE UPLOAD (Bypass Cloud Armor)
    // Check if image is a Data URI (Base64) - check for 'data:' prefix generally
    if (
      payload.image &&
      (payload.image.startsWith("data:") || payload.image.length > 500)
    ) {
      try {
        console.log(
          "🚀 Detected potential base64 image. Prefix:",
          payload.image.substring(0, 30)
        );

        // Extract mimetype from data URI if possible
        const mimeMatch = payload.image.match(/data:([^;]+);/);
        const mimeType = mimeMatch ? mimeMatch[1] : "image/png"; // Default to png if not found

        console.log("🚀 Starting chunked profile image upload...");

        setUploadProgress({
          show: true,
          current: 0,
          total: 0,
          label: "Uploading profile image...",
        });
        const imageFileId = await dispatch(
          uploadFileChunked({
            fileData: payload.image,
            fileName: "profile_image.png",
            mimeType,
            onProgress: ({ current, total }) =>
              setUploadProgress({
                show: true,
                current,
                total,
                label: "Uploading profile image...",
              }),
          })
        ).unwrap();
        setUploadProgress({ show: false, current: 0, total: 0, label: "" });

        console.log("✅ Profile image uploaded chunked:", imageFileId);
        payload.image = imageFileId; // Replace base64 with ID for backend resolution
      } catch (uploadErr) {
        console.error("Profile image upload failed:", uploadErr);
        Toast({
          message: "Failed to upload profile image. Please try again.",
          type: "error",
          onClose: () => {},
        });
        isSaveOperationRef.current = false;
        return;
      }
    }

    const addCardResult = await dispatch(addCareerCard(payload));

    let newEmpDetailsUid;

    if (
      typeof addCardResult.payload === "object" &&
      addCardResult.payload !== null &&
      "data" in addCardResult.payload &&
      typeof addCardResult.payload.data === "object" &&
      addCardResult.payload.data !== null
    ) {
      if (
        "data" in addCardResult.payload.data &&
        typeof addCardResult.payload.data.data === "object" &&
        addCardResult.payload.data.data !== null &&
        "emp_details_uid" in addCardResult.payload.data.data
      ) {
        newEmpDetailsUid = addCardResult.payload.data.data
          .emp_details_uid as number;
      } else if ("emp_details_uid" in addCardResult.payload.data) {
        newEmpDetailsUid = addCardResult.payload.data.emp_details_uid as number;
      }
    }

    const mode =
      data?.emp_work_experiences?.some(
        (exp: any) => exp.emp_work_experience_uid
      ) || data?.emp_languages?.some((l: any) => l.emp_languages_uid)
        ? "edit"
        : "add";

    const otherDetailsPayload = buildOtherDetailsPayload(
      mode,
      newEmpDetailsUid
    );

    const otherDetailsResult = await dispatch(
      addCareerCardOtherDetails(otherDetailsPayload)
    );

    window.scrollTo({ top: 0, behavior: "smooth" });

    if (empOracleDetails.data && empDetails.data) {
      try {
        const pdfResult = await generateBulkPDFBackground(data, false);

        if (!pdfResult) {
          return;
        }

        const emailTo = (() => {
          const userId = data?.email_id;
          const managerEmail = data?.manager_email;

          const hasValidManagerEmail =
            managerEmail && managerEmail.trim() !== "";

          if (emailMe && emailManager && hasValidManagerEmail) {
            return `${userId},${managerEmail}`;
          } else if (emailMe) {
            return userId;
          } else if (emailManager && hasValidManagerEmail) {
            return managerEmail;
          } else {
            return null;
          }
        })();
        const currentYear = new Date().getFullYear();

        if (emailTo && (emailMe || emailManager)) {
          // Construct JSON payload to bypass Cloud Armor WAF "Protocol Attack" rules on multipart
          const subject =
            "b64:" +
            window.btoa(
              unescape(
                encodeURIComponent("Career Card Submission - " + data?.name)
              )
            );

          const messageBody = `<!DOCTYPE html><html><body><p>Dear ${data?.name}, <br><br>Please find your ${currentYear} Career Card attached.<br><br>Thank you, <br>Career Card Team <br><br><strong>Note:</strong> This is an automated message. Please do not reply.</p></body></html>`;
          const message =
            "b64:" + window.btoa(unescape(encodeURIComponent(messageBody)));

          const emailPayload: any = {
            emailTo,
            subject,
            message,
          };

          // Upload PDF via chunked upload if needed
          const pdfFile = new File([pdfResult.blob], pdfResult.fileName, {
            type: "application/pdf",
            lastModified: Date.now(),
          });

          try {
            console.log("🚀 Starting chunked PDF upload...");

            setUploadProgress({
              show: true,
              current: 0,
              total: 0,
              label: "Uploading PDF attachment...",
            });
            const fileId = await dispatch(
              uploadFileChunked({
                fileData: pdfFile,
                fileName: pdfResult.fileName,
                mimeType: "application/pdf",
                onProgress: ({ current, total }) =>
                  setUploadProgress({
                    show: true,
                    current,
                    total,
                    label: "Uploading PDF attachment...",
                  }),
              })
            ).unwrap();
            setUploadProgress({ show: false, current: 0, total: 0, label: "" });

            console.log("✅ PDF uploaded chunked:", fileId);
            emailPayload.attachment = fileId; // Send ID string, NOT file object
          } catch (uploadError) {
            console.error("Failed to upload PDF attachment:", uploadError);
          }

          await sendEmail(emailPayload);
        }
      } catch (error) {
        console.error("Error in sendCareerCardEmail:", error);
      }
    }

    // If the second API call was successful, refresh employee details
    if (addCareerCardOtherDetails.fulfilled.match(otherDetailsResult)) {
      handleFetchWithProgress("Loading profile");
    }

    // Force dropdown component re-render after save
    setEducationDropdownKey((prev) => prev + 1);

    setIsEditing(false);
    setEmailMe(false);
    setEmailManager(false);

    // Clear save operation flag
    isSaveOperationRef.current = false;
  };

  function handleExportToExcel() {
    exportCareercardToExcel(data);
  }

  function handleExportToPDF() {
    generateBulkPDFBackground(data, true);
  }

  const importExcelFileInputRef = useRef<HTMLInputElement>(null);

  const handleImportFromExcel = () => {
    if (importExcelFileInputRef.current) {
      importExcelFileInputRef.current.click();
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // File size validation (limit to 10MB)
    const maxSizeInBytes = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSizeInBytes) {
      showToast(
        "File size too large. Please upload a file smaller than 10MB.",
        "error"
      );
      if (importExcelFileInputRef.current) {
        importExcelFileInputRef.current.value = "";
      }
      return;
    }

    // Additional validation for empty files
    if (file.size === 0) {
      showToast(
        "Cannot upload empty file. Please select a valid Excel file.",
        "error"
      );
      if (importExcelFileInputRef.current) {
        importExcelFileInputRef.current.value = "";
      }
      return;
    }

    const allowedTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "application/zip",
    ];

    if (!allowedTypes.includes(file.type)) {
      showToast("Please upload a valid Excel file (.xlsx, .xls)", "error");
      // Clear the file input for invalid files
      if (importExcelFileInputRef.current) {
        importExcelFileInputRef.current.value = "";
      }
      return;
    }

    // Capture current data before import
    capturePreImportData();

    // Use chunked upload to bypass Cloud Armor XSS detection
    const { importFromExcelChunkedAPICall } = await import(
      "./ImportFromExcelChunkedSlice"
    );
    setUploadProgress({
      show: true,
      current: 0,
      total: 0,
      label: "Uploading Excel file...",
    });
    await dispatch(
      importFromExcelChunkedAPICall({
        file,
        onProgress: ({ current, total }) =>
          setUploadProgress({
            show: true,
            current,
            total,
            label: "Uploading Excel file...",
          }),
      })
    );
    setUploadProgress({ show: false, current: 0, total: 0, label: "" });
    // Loader UI for chunked uploads
    const ChunkUploadProgressLoader = () =>
      uploadProgress.show ? (
        <div className="loading-backdrop">
          <div
            className="loading-modal"
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "rgba(255, 255, 255, 0.98)",
              color: "#333333",
              padding: "30px 40px",
              borderRadius: "12px",
              border: "2px solid rgb(244, 247, 249)",
              boxShadow: "0 15px 35px rgba(0, 0, 0, 0.3)",
              zIndex: 9999,
              textAlign: "center",
              fontSize: "16px",
              fontWeight: "500",
              minWidth: "300px",
            }}
          >
            <div className="mb-3">
              <div
                className="spinner-border text-primary mb-2"
                style={{ width: "2.5rem", height: "2.5rem" }}
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="fw-bold">{uploadProgress.label}</div>
            </div>
            <div className="mb-2">
              <div className="progress" style={{ height: "8px" }}>
                <div
                  className="progress-bar bg-primary"
                  style={{
                    width:
                      uploadProgress.total > 0
                        ? `${
                            (uploadProgress.current / uploadProgress.total) *
                            100
                          }%`
                        : "0%",
                    transition: "width 0.5s ease",
                  }}
                ></div>
              </div>
            </div>
            <div className="small text-muted">
              {uploadProgress.current} of {uploadProgress.total} chunks
            </div>
          </div>
        </div>
      ) : null;
    // Render the chunk upload loader at the root of the component
    <ChunkUploadProgressLoader />;
  };

  const importFromExcelState = useSelector(
    (state: RootState) => state.importFromExcelAPICall
  );
  const importAPIState = importFromExcelState?.status;

  // Handle Excel import success toast
  useEffect(() => {
    // Only process if the status has changed and it's not the same as the last processed status
    if (importAPIState && importAPIState !== lastImportStatusRef.current) {
      if (importAPIState === "succeeded") {
        setShowImportErrorBanner(false);
        setImportValidationErrors([]);
        setImportErrorMessage("");
        // Clear the file input to allow re-uploading the same file
        if (importExcelFileInputRef.current) {
          importExcelFileInputRef.current.value = "";
        }
        // Reset the import status to prevent re-triggering
        setTimeout(() => {
          dispatch(resetImportFromExcel());
        }, 100);
        // Refresh the employee data to show the imported changes
        setTimeout(() => {
          if (empId) {
            // Set flag to trigger comparison after data refresh
            needsComparisonRef.current = true;

            // Reset current data states to force refresh
            dispatch(resetEmpDetails());
            dispatch(resetEmpOracleDetails());
            // Re-fetch the updated data
            dispatch(fetchEmpDetails(empId));
            dispatch(fetchEmpOracleDetails(empId));
          }
        }, 500);
      } else if (importAPIState === "failed") {
        const errorMessage = importFromExcelState?.error || "";
        const fieldErrors = (importFromExcelState as any)?.fieldErrors || [];

        if (fieldErrors.length > 0) {
          setImportValidationErrors(fieldErrors);
          setImportErrorMessage(errorMessage);
          setShowImportErrorBanner(true);
        } else if (errorMessage.includes("Blocked special characters found") || errorMessage.includes("invalid characters") || errorMessage.includes("Special characters") || errorMessage.includes("validation")) {
          
          setImportValidationErrors([]);
          setImportErrorMessage(
            errorMessage || "Excel file validation failed. Please correct the data and try again."
          );
          setShowImportErrorBanner(true);
        } else {
          showToast(
            errorMessage || "Failed to import Excel file. Please check the file format and try again.",
            "error"
          );
        }
        // Clear the file input on error as well
        if (importExcelFileInputRef.current) {
          importExcelFileInputRef.current.value = "";
        }
        // Reset the import status after error
        setTimeout(() => {
          dispatch(resetImportFromExcel());
        }, 100);
        // Clear any captured pre-import data on error
        preImportDataRef.current = null;
      }
      // Update the ref to track the last processed status
      lastImportStatusRef.current = importAPIState;
    }
  }, [importAPIState, showToast, dispatch, empId, compareAndHighlightChanges]);

  // Updated generateBulkPDFBackground function for single data object:
  const generateBulkPDFBackground = async (
    data: any,
    isExport: boolean
  ): Promise<{ blob: Blob; fileName: string } | null> => {
    if (!data) {
      return null;
    }

    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "in",
      format: [14.65, 12.32],
      compress: true,
    });
    if (isExport) {
      setPdfProgress({ show: true, current: 0, total: 1 });
    }

    try {
      if (isExport) {
        setPdfProgress((prev) => ({ ...prev, current: 1 }));
      }

      const offscreenDiv = document.createElement("div");
      offscreenDiv.style.position = "absolute";
      offscreenDiv.style.left = "-9999px";
      offscreenDiv.style.top = "-9999px";
      offscreenDiv.style.width = "1700px";
      offscreenDiv.style.fontSize = "13px";
      offscreenDiv.style.lineHeight = "1.2";
      offscreenDiv.style.color = "#000";
      offscreenDiv.style.backgroundColor = "#fff";
      offscreenDiv.style.padding = "50px 100px 50px 100px";
      offscreenDiv.style.visibility = "visible";

      document.body.appendChild(offscreenDiv);

      const { createRoot } = await import("react-dom/client");
      const root = createRoot(offscreenDiv);

      await new Promise<void>((resolve) => {
        root.render(React.createElement(CareerCardHtml, { data: data }));
        requestAnimationFrame(() => {
          setTimeout(() => resolve(), 300);
        });
      });

      const canvas = await html2canvas(offscreenDiv, {
        scale: 1.5,
        useCORS: true,
        backgroundColor: "#fff",
        logging: false,
        allowTaint: false,
        removeContainer: false,
        height: offscreenDiv.scrollHeight,
        width: offscreenDiv.scrollWidth,
        onclone: (clonedDoc: any) => {
          const clonedElement = clonedDoc.body.querySelector("div");
          if (clonedElement) {
            clonedElement.style.visibility = "visible";
            clonedElement.style.display = "block";
          }
        },
      });

      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error(
          "Canvas is empty. Component may not have rendered properly."
        );
      }

      const imgData = canvas.toDataURL("image/jpeg", 1);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgProps = pdf.getImageProperties(imgData);
      const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

      if (imgHeight > pdfHeight) {
        const scaledHeight = pdfHeight;
        const scaledWidth = (imgProps.width * scaledHeight) / imgProps.height;
        pdf.addImage(
          imgData,
          "JPEG",
          (pdfWidth - scaledWidth) / 2,
          0,
          scaledWidth,
          scaledHeight
        );
      } else {
        pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, imgHeight);
      }

      const fileName = `Career_Card_${
        data?.name?.replace(/\s+/g, "_") || "Export"
      }_${new Date().toISOString().split("T")[0].replace(/-/g, "")}.pdf`;
      const blob = pdf.output("blob");

      if (isExport) {
        pdf.save(fileName);
      }
      // Cleanup
      root.unmount();
      document.body.removeChild(offscreenDiv);
      canvas.remove();

      return { blob, fileName };
    } catch (error) {
      console.error("PDF generation error:", error);
      return null;
    } finally {
      if (isExport) {
        setPdfProgress({ show: false, current: 0, total: 0 });
      }
    }
  };

  // Toggle section expansion
  const toggleSection = (sectionId: string): void => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  // Expand all sections
  const expandAllSections = (): void => {
    setExpandedSections(new Set(CAREER_CARD_SECTIONS.map((s) => s.id)));
  };

  // Collapse all sections
  const collapseAllSections = (): void => {
    setExpandedSections(new Set());
  };

  const renderSectionContent = (sectionId: string): React.ReactNode => {
    const contentMap: Record<string, React.ReactNode> = {
      "employee-data": (
        <div className="clsempdtls mb-3">
          <div className="col-md-2 position-relative clsimage">
            <div style={{ position: "relative", width: "100%" }}>
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt="Avatar"
                  className={`img-thumbnail rounded-circle avatar mb-2${
                    isEditing && !showImageInput ? " image-blur" : ""
                  }`}
                  style={{
                    filter:
                      isEditing && !showImageInput
                        ? "blur(2px) grayscale(0.5)"
                        : "none",
                    transition: "filter 0.2s",
                  }}
                />
              ) : (
                <div
                  className={`img-thumbnail rounded-circle avatar mb-2 d-flex align-items-center justify-content-center${
                    isEditing && !showImageInput ? " image-blur" : ""
                  }`}
                  style={{
                    backgroundColor: "#e9ecef",
                    filter:
                      isEditing && !showImageInput
                        ? "blur(2px) grayscale(0.5)"
                        : "none",
                    transition: "filter 0.2s",
                  }}
                >
                  <i
                    className="bi bi-person-fill"
                    style={{ fontSize: "3rem", color: "#6c757d" }}
                    aria-hidden="true"
                  ></i>
                </div>
              )}
              {isEditing && !showImageInput && (
                <button
                  type="button"
                  className="btn btn-light btn-sm position-absolute top-50 start-50 translate-middle shadow"
                  style={{ borderRadius: "50%", padding: 6, zIndex: 2 }}
                  onClick={handleEditImageClick}
                  aria-label="Edit Image"
                >
                  <i
                    className="pi pi-pencil"
                    style={{ fontSize: "1.2rem" }}
                  ></i>
                </button>
              )}
              {showImageInput && (
                <div className="mt-2">
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={handleImageFileChange}
                    className="form-control form-control-sm"
                  />
                  {imageWarning && (
                    <div className="text-danger small mt-1">{imageWarning}</div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="col-md-10">
            <div className="container">
              {/* Row 1 */}
              <div
                className={
                  isEditing
                    ? "row text-secondary fw-light mb-3 g-3"
                    : "clsempdtls text-secondary fw-light mb-3"
                }
              >
                <div className="col-md-4">
                  <div className="labelheading">Name</div>
                  {isEditing ? (
                    <>
                      <input
                        name="name"
                        value={data?.name || ""}
                        onChange={handleChange}
                        className={`form-control form-control-sm${
                          formErrors.name ? " is-invalid" : ""
                        }`}
                        required
                      />
                      {formErrors.name && (
                        <div className="invalid-feedback d-block">
                          {formErrors.name}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="clsValues">{data?.name || ""}</div>
                  )}
                </div>
                <div className="col-md-4">
                  <div className="labelheading">Oracle ID</div>
                  <div className={isEditing ? "clsValues-disabled" : "clsValues"}>{data?.oracle_id || ""}</div>
                </div>
                <div className="col-md-4">
                  <div className="labelheading">Job Title</div>
                  <div className={isEditing ? "clsValues-disabled" : "clsValues"}>{data?.jobTitle || ""}</div>
                </div>
              </div>

              {/* Row 2 */}
              <div
                className={
                  isEditing
                    ? "row text-secondary fw-light mb-3 g-3"
                    : "clsempdtls text-secondary fw-light mb-3"
                }
              >
                <div className="col-md-4">
                  <div className="labelheading">Education/Degree</div>
                  {isEditing ? (
                    <div>
                      <div className="education-dropdown-container">
                        <EducationDropDown
                          key={`education-dropdown-${educationDropdownKey}-${isEditing}`}
                          value={educationLevel || ""}
                          options={EDUCATION_OPTIONS}
                          onChange={handleEducationLevelChange}
                          placeholder="Select Education Level"
                          className={`w-100 p-2 ${
                            formErrors.education_degree ? "p-invalid" : ""
                          }`}
                          showClear={false}
                          panelClassName="education-dropdown-panel"
                          resetFilterOnHide={true}
                          filter={false}
                          disabled={!isEditing}
                        />
                      </div>
                      {showEducationOther && (
                        <div>
                          <input
                            type="text"
                            name="education_degree"
                            value={educationOther}
                            onChange={handleEducationOtherChange}
                            placeholder="Please specify other education"
                            className={`form-control form-control-sm mt-2 ${getFieldError('education_degree') ? 'is-invalid' : ''}`}
                            maxLength={100}
                          />
                          {getFieldError('education_degree') && (
                            <small className="text-danger d-block">{VALIDATION_ERROR_MESSAGE}</small>
                          )}
                        </div>
                      )}
                      {formErrors.education_degree && (
                        <div className="invalid-feedback d-block">
                          {formErrors.education_degree}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="clsValues">
                      {educationLevel && educationLevel !== "other" && (
                        <>
                          {
                            EDUCATION_OPTIONS.find(
                              (opt) => opt.value === educationLevel
                            )?.label
                          }
                          ,
                        </>
                      )}{" "}
                      {data?.education_degree || ""}
                    </div>
                  )}
                </div>
                <div className="col-md-4">
                  <div className="labelheading">
                    Time in Current Role
                    <TooltipIcon tooltipText={getTooltipById("time-in-current-role")} />
                  </div>
                  {isEditing ? (
                    <>
                      <input
                        name="time_in_current_role"
                        value={data?.time_in_current_role || ""}
                        onChange={handleChange}
                        placeholder="e.g., 2 years 6 months"
                        className={`form-control form-control-sm${
                          formErrors.time_in_current_role ? " is-invalid" : ""
                        }${getFieldError('time_in_current_role') ? " is-invalid" : ""}`}
                        required
                      />
                      {getFieldError('time_in_current_role') && (
                        <small className="text-danger d-block">{VALIDATION_ERROR_MESSAGE}</small>
                      )}
                      {formErrors.time_in_current_role && (
                        <div className="invalid-feedback d-block">
                          {formErrors.time_in_current_role}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="clsValues">
                      {data?.time_in_current_role || ""}
                    </div>
                  )}
                </div>
                <div className="col-md-4">
                  <div className="labelheading">Time at TTEC</div>
                  <div className={isEditing ? "clsValues-disabled" : "clsValues"}>{data?.timeAtTTEC || ""}</div>
                </div>
              </div>

              {/* Row 3 */}
              <div
                className={
                  isEditing
                    ? "row text-secondary fw-light mb-3 g-3"
                    : "clsempdtls text-secondary fw-light mb-3"
                }
              >
                <div className="col-md-4">
                  <div className="labelheading">Languages</div>
                  {isEditing ? (
                    <div className="custom-multiselect-wrapper">
                      <MultiSelect
                        value={languages || []}
                        options={languageOptions.map((opt: { label: string; value: number }) => ({
                          ...opt,
                          disabled: isLanguageLimitReached && !languages.includes(opt.value),
                        }))}
                        onChange={(e) => {
                          if (!e.value || e.value.length === 0) {
                            setLanguages([]);
                            setData((prevData: any) => ({
                              ...prevData,
                              languages: "",
                              emp_languages: [],
                            }));
                          } else {
                            handleLanguagesChange(e);
                          }
                        }}
                        optionLabel="label"
                        optionValue="value"
                        optionDisabled="disabled"
                        display="chip"
                        placeholder="Select Languages"
                        className="w-100"
                        showClear={true}
                        resetFilterOnHide={true}
                        filter
                        filterPlaceholder="Search languages..."
                        panelClassName="languages-multiselect-panel"
                        maxSelectedLabels={0}
                        selectedItemsLabel={getSelectedItemsLabel(languages, languageOptions, 'languages')}
                      />
                      {isLanguageLimitReached && (
                        <div className="language-limit-warning">
                          <i className="bi bi-exclamation-triangle-fill"></i>
                          <span>{LANGUAGE_LIMIT_WARNING}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="clsValues">
                      {Array.isArray(data?.emp_languages) &&
                      data.emp_languages.length > 0
                        ? renderViewList(
                            data.emp_languages,
                            'languages',
                            (l: any) => l.language_dropdown?.language || l.language
                          )
                        : "-"}
                    </div>
                  )}
                </div>
                <div className="col-md-4">
                  <div className="labelheading">
                    Certifications
                    <TooltipIcon tooltipText={getTooltipById("certifications")} />
                  </div>
                  {isEditing ? (
                    <>
                      <input
                        name="certificates"
                        value={data?.certificates || ""}
                        onChange={handleChange}
                        placeholder="e.g., AWS Certified, Microsoft Azure, PMP"
                        className={`form-control form-control-sm${
                          formErrors.certificates ? " is-invalid" : ""
                        }${getFieldError('certificates') ? " is-invalid" : ""}`}
                        required
                      />
                      {getFieldError('certificates') && (
                        <small className="text-danger d-block">{VALIDATION_ERROR_MESSAGE}</small>
                      )}
                      {formErrors.certificates && (
                        <div className="invalid-feedback d-block">
                          {formErrors.certificates}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="clsValues">{data?.certificates || ""}</div>
                  )}
                </div>
                <div className="col-md-4">
                  <div className="labelheading">Job Level</div>
                  <div className={isEditing ? "clsValues-disabled" : "clsValues"}>{data?.jobLevel || ""}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),

      "location-mobility": (
        <div className="container">
          <div className="row text-secondary fw-light">
            <div className="col-md-3">
              <div className="labelheading">Current Location</div>
              <div className={isEditing ? "clsValues-disabled" : "clsValues"}>{data?.location_desc || ""}</div>
            </div>
            <div className="col-md-3">
              <div className="labelheading">Willingness to Relocate</div>
              {isEditing ? (
                <select
                  id="willingness_to_relocate"
                  name="willingnessToRelocate"
                  value={data?.willingness_to_relocate || "Yes"}
                  onChange={handleWillingnessChange}
                  className={`form-control form-control-sm ${getFieldHighlightClass(
                    "willingness_to_relocate"
                  )}`}
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              ) : (
                <div className="clsValues">
                  {data?.willingness_to_relocate || ""}
                </div>
              )}
            </div>
            <div className="col-md-5">
              <div className="labelheading">
                Location Mobility
                <TooltipIcon
                  tooltipText={getTooltipById("location-preferences")}
                />
              </div>
              {isEditing ? (
                <div className="custom-multiselect-wrapper">
                  <MultiSelect
                    value={locationPreferences || []}
                    options={locationOptions}
                    onChange={handleLocationPreferencesChange}
                    optionLabel="label"
                    optionValue="value"
                    display="chip"
                    placeholder="Select Location Preferences"
                    className="w-100"
                    showClear={true}
                    resetFilterOnHide={true}
                    panelClassName="languages-multiselect-panel"
                    maxSelectedLabels={0}
                    selectedItemsLabel={getSelectedItemsLabel(locationPreferences, locationOptions, 'locations')}
                  />
                </div>
              ) : (
                <div>
                  <div className="clsValues">
                    {Array.isArray(data?.EmpLocationPreferences) &&
                    data.EmpLocationPreferences.length > 0
                      ? renderViewList(
                          data.EmpLocationPreferences,
                          'locations',
                          (loc: any) => loc.location_dropdown?.location || loc.location
                        )
                      : "-"}
                  </div>
                </div>
              )}
            </div>
            {(isEditing && showLocationOthersInput) ||
            (!isEditing && locationOthersComments) ? (
              <div className="col-md-3">
                <div className="labelheading">Other Location Preferences</div>
                {isEditing ? (
                  <textarea
                    className="form-control"
                    placeholder="Please specify other location preferences"
                    value={locationOthersComments}
                    onChange={handleLocationOthersCommentsChange}
                    rows={2}
                  />
                ) : (
                  <div className="clsValues">
                    {locationOthersComments || "-"}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      ),

      "role-aspirations": (
        <div className="container">
          <div className="row text-secondary fw-light">
            <div className="col-md-3">
              <div className="labelheading">
                Your next role preference
                <TooltipIcon tooltipText={getTooltipById("additional-roles")} />
              </div>
              {isEditing ? (
                <>
                  <input
                    name="next_role_preference"
                    value={data?.next_role_preference || ""}
                    onChange={handleChange}
                    className={`form-control form-control-sm${
                      formErrors.next_role_preference ? " is-invalid" : ""
                    }${getFieldError('next_role_preference') ? " is-invalid" : ""}`}
                    required
                  />
                  {getFieldError('next_role_preference') && (
                    <small className="text-danger d-block">{VALIDATION_ERROR_MESSAGE}</small>
                  )}
                </>
              ) : (
                <div className="clsValues">
                  {data?.next_role_preference || ""}
                </div>
              )}
            </div>
            <div className="col-md-3">
              <div className="labelheading">
                Future career aspirations
                <TooltipIcon tooltipText={getTooltipById("additional-roles")} />
              </div>
              {isEditing ? (
                <>
                  <input
                    name="future_career_aspirations"
                    value={
                      (data && data.future_career_aspirations !== null
                        ? data.future_career_aspirations
                        : "") || ""
                    }
                    onChange={handleChange}
                    className={`form-control form-control-sm${getFieldError('future_career_aspirations') ? " is-invalid" : ""}`}
                  />
                  {getFieldError('future_career_aspirations') && (
                    <small className="text-danger d-block">{VALIDATION_ERROR_MESSAGE}</small>
                  )}
                </>
              ) : (
                <div className="clsValues">
                  {(data && data.future_career_aspirations !== null
                    ? data.future_career_aspirations
                    : "") || ""}
                </div>
              )}
            </div>
            <div className="col-md-3">
              <div className="labelheading">
                Your ideal timing for next role
                <TooltipIcon tooltipText={getTooltipById("role-readiness")} />
              </div>
              {isEditing ? (
                <select
                  id="timing_for_next_role"
                  name="timing_for_next_role"
                  value={data?.timing_for_next_role || "Ready Now"}
                  onChange={handleTimingChange}
                  className={`form-control form-control-sm ${getFieldHighlightClass(
                    "timing_for_next_role"
                  )}`}
                >
                  {[
                    "Ready Now",
                    "One to Two Years",
                    "Two to Five Years",
                    "Five Years Plus",
                  ].map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="clsValues">
                  {data?.timing_for_next_role || ""}
                </div>
              )}
            </div>
          </div>
        </div>
      ),

      experience: (
        <div className="container">
          {/* Add Experience Button */}
          {isEditing && (
            <div className="mb-2">
              <button
                className={`btn btn-sm ${
                  unifiedExperiences.length >= 4
                    ? "btn-secondary"
                    : "btn-primary"
                }`}
                type="button"
                onClick={handleAddUnifiedExperience}
                disabled={unifiedExperiences.length >= 4}
              >
                <i className="bi bi-plus-circle me-1"></i>
                Add Experience ({4 - unifiedExperiences.length} remaining)
              </button>
            </div>
          )}

          {/* Clean Grid Table - No Lines */}
          <table className="table table-sm" style={{ border: "none" }}>
            <thead>
              <tr style={{ border: "none" }}>
                <th
                  style={{
                    width: "12%",
                    textAlign: "center",
                    border: "none",
                    paddingBottom: "12px",
                    fontWeight: "600",
                  }}
                >
                  TTEC/External
                </th>
                <th
                  style={{
                    width: "20%",
                    textAlign: "center",
                    border: "none",
                    paddingBottom: "12px",
                    fontWeight: "600",
                  }}
                >
                  Role Title
                </th>
                <th
                  style={{
                    width: "15%",
                    textAlign: "center",
                    border: "none",
                    paddingBottom: "12px",
                    fontWeight: "600",
                  }}
                >
                  Company
                </th>
                <th
                  style={{
                    width: "10%",
                    textAlign: "center",
                    border: "none",
                    paddingBottom: "12px",
                    fontWeight: "600",
                  }}
                >
                  Start Year
                </th>
                <th
                  style={{
                    width: "10%",
                    textAlign: "center",
                    border: "none",
                    paddingBottom: "12px",
                    fontWeight: "600",
                  }}
                >
                  End Year
                </th>
                <th
                  style={{
                    width: "33%",
                    textAlign: "center",
                    border: "none",
                    paddingBottom: "12px",
                    fontWeight: "600",
                  }}
                >
                  Description
                </th>
                {isEditing && (
                  <th
                    style={{
                      width: "5%",
                      textAlign: "center",
                      border: "none",
                      paddingBottom: "12px",
                      fontWeight: "600",
                    }}
                  >
                    Action
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {unifiedExperiences.length === 0 ? (
                <tr>
                  <td
                    colSpan={isEditing ? 7 : 6}
                    className="text-center text-muted py-3"
                    style={{ border: "none" }}
                  >
                    No experiences added yet
                  </td>
                </tr>
              ) : (
                unifiedExperiences.map((exp, idx) => (
                  <tr key={idx} style={{ border: "none" }}>
                    {/* Type Column */}
                    <td
                      style={{
                        textAlign: "center",
                        verticalAlign: "middle",
                        border: "none",
                        padding: "12px 8px",
                      }}
                    >
                      {isEditing ? (
                        <div className="form-check form-switch d-flex justify-content-center">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={exp?.isTTEC || false}
                            onChange={(e) =>
                              handleUnifiedExperienceChange(
                                idx,
                                "isTTEC",
                                e.target.checked
                              )
                            }
                            id={`ttecExp${idx}`}
                          />
                          <label
                            className="form-check-label small ms-1"
                            htmlFor={`ttecExp${idx}`}
                          >
                            {exp?.isTTEC ? "TTEC" : "External"}
                          </label>
                        </div>
                      ) : (
                        <span
                          className={`badge ${
                            exp?.isTTEC ? "bg-primary" : "bg-secondary"
                          }`}
                        >
                          {exp?.isTTEC ? "TTEC" : "External"}
                        </span>
                      )}
                    </td>

                    {/* Role Title Column */}
                    <td
                      style={{
                        textAlign: "center",
                        verticalAlign: "middle",
                        border: "none",
                        padding: "12px 8px",
                      }}
                    >
                      {isEditing ? (
                        <div>
                          <input
                            name={`experience_${idx}_roleTitle`}
                            value={exp?.roleTitle || ""}
                            onChange={(e) =>
                              handleUnifiedExperienceChange(
                                idx,
                                "roleTitle",
                                e.target.value
                              )
                            }
                            className={`form-control form-control-sm text-center${getFieldError(`experience_${idx}_roleTitle`) ? " is-invalid" : ""}`}
                            placeholder="Role title"
                          />
                          {getFieldError(`experience_${idx}_roleTitle`) && (
                            <small className="text-danger d-block">{VALIDATION_ERROR_MESSAGE}</small>
                          )}
                        </div>
                      ) : (
                        <span>{exp?.roleTitle || "-"}</span>
                      )}
                    </td>

                    {/* Company Column */}
                    <td
                      style={{
                        textAlign: "center",
                        verticalAlign: "middle",
                        border: "none",
                        padding: "12px 8px",
                      }}
                    >
                      {isEditing ? (
                        <div>
                          <input
                            name={`experience_${idx}_companyName`}
                            value={exp?.companyName || ""}
                            onChange={(e) =>
                              handleUnifiedExperienceChange(
                                idx,
                                "companyName",
                                e.target.value
                              )
                            }
                            className={`form-control form-control-sm text-center${getFieldError(`experience_${idx}_companyName`) ? " is-invalid" : ""}`}
                            placeholder={exp?.isTTEC ? "TTEC" : "Company"}
                            disabled={exp?.isTTEC}
                          />
                          {getFieldError(`experience_${idx}_companyName`) && (
                            <small className="text-danger d-block">{VALIDATION_ERROR_MESSAGE}</small>
                          )}
                        </div>
                      ) : (
                        <span>{exp?.companyName || "-"}</span>
                      )}
                    </td>

                    {/* Start Year Column */}
                    <td
                      style={{
                        textAlign: "center",
                        verticalAlign: "middle",
                        border: "none",
                        padding: "12px 8px",
                      }}
                    >
                      {isEditing ? (
                        <div>
                          <input
                            type="text"
                            value={(() => {
                              if (!exp?.startYear) return "";
                              if (/^\d{1,4}$/.test(exp.startYear)) {
                                return exp.startYear;
                              }
                              const date = new Date(exp.startYear);
                              return date.toString() !== "Invalid Date"
                                ? date.getFullYear().toString()
                                : "";
                            })()}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || /^\d{0,4}$/.test(value)) {
                                handleUnifiedExperienceChange(
                                  idx,
                                  "startYear",
                                  value
                                );
                              }
                            }}
                            className={`form-control form-control-sm text-center ${
                              formErrors[`experience${idx}Dates`] || formErrors[`experience${idx}StartYear`]
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="Year"
                            maxLength={4}
                          />
                          {formErrors[`experience${idx}StartYear`] && (
                            <small className="text-danger d-block mt-1">Future year not allowed</small>
                          )}
                        </div>
                      ) : (
                        <span>{displayYearFromDate(exp?.startYear)}</span>
                      )}
                    </td>

                    {/* End Year Column */}
                    <td
                      style={{
                        textAlign: "center",
                        verticalAlign: "middle",
                        border: "none",
                        padding: "12px 8px",
                      }}
                    >
                      {isEditing ? (
                        <div className="d-flex flex-column align-items-center">
                          <div className="d-flex align-items-center justify-content-center gap-2">
                            {exp?.currentRole ? (
                              <span className="badge bg-success" style={{ minWidth: "70px" }}>Present</span>
                            ) : (
                              <input
                                type="text"
                                value={(() => {
                                  if (!exp?.endYear) return "";
                                  if (/^\d{1,4}$/.test(exp.endYear)) {
                                    return exp.endYear;
                                  }
                                  const date = new Date(exp.endYear);
                                  return date.toString() !== "Invalid Date"
                                    ? date.getFullYear().toString()
                                    : "";
                                })()}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === "" || /^\d{0,4}$/.test(value)) {
                                    handleUnifiedExperienceChange(
                                      idx,
                                      "endYear",
                                      value
                                    );
                                  }
                                }}
                                className={`form-control form-control-sm text-center ${
                                  formErrors[`experience${idx}Dates`] || formErrors[`experience${idx}EndYear`]
                                    ? "is-invalid"
                                    : ""
                                }`}
                                placeholder="Year"
                                maxLength={4}
                                style={{ width: "70px" }}
                              />
                            )}
                            <div className="form-check form-switch mb-0 ps-5">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={exp?.currentRole || false}
                                onChange={(e) =>
                                  handleUnifiedExperienceChange(
                                    idx,
                                    "currentRole",
                                    e.target.checked
                                  )
                                }
                                id={`currentRole${idx}`}
                                role="switch"
                              />
                              <label
                                className="form-check-label small"
                                htmlFor={`currentRole${idx}`}
                              >
                                Current
                              </label>
                            </div>
                          </div>
                          {formErrors[`experience${idx}EndYear`] && (
                            <small className="text-danger mt-1">Future year not allowed</small>
                          )}
                        </div>
                      ) : (
                        <span>
                          {exp?.currentRole ? (
                            <span className="badge bg-success">Present</span>
                          ) : (
                            displayYearFromDate(exp?.endYear)
                          )}
                        </span>
                      )}
                    </td>

                    {/* Description Column - Center Aligned */}
                    <td
                      style={{
                        textAlign: "center",
                        verticalAlign: "middle",
                        border: "none",
                        padding: "12px 8px",
                      }}
                    >
                      {isEditing ? (
                        <div>
                          <textarea
                            name={`experience_${idx}_roleDescription`}
                            value={exp?.roleDescription || ""}
                            onChange={(e) =>
                              handleUnifiedExperienceChange(
                                idx,
                                "roleDescription",
                                e.target.value
                              )
                            }
                            className={`form-control form-control-sm text-center${getFieldError(`experience_${idx}_roleDescription`) ? " is-invalid" : ""}`}
                            placeholder="Description"
                            rows={3}
                          />
                          {getFieldError(`experience_${idx}_roleDescription`) && (
                            <small className="text-danger d-block">{VALIDATION_ERROR_MESSAGE}</small>
                          )}
                        </div>
                      ) : (
                        <span className="text-wrap">
                          {exp?.roleDescription || "-"}
                        </span>
                      )}
                    </td>

                    {/* Action Column */}
                    {isEditing && (
                      <td
                        style={{
                          textAlign: "center",
                          verticalAlign: "middle",
                          border: "none",
                          padding: "12px 8px",
                        }}
                      >
                        {unifiedExperiences.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => handleRemoveUnifiedExperience(idx)}
                            title="Delete"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Validation Errors */}
          {(formErrors.totalExperienceCount ||
            formErrors.currentPositionCount ||
            formErrors.externalCurrentRole) && (
            <div className="alert alert-danger alert-sm mt-2 py-2">
              {formErrors.totalExperienceCount && (
                <div className="small">{formErrors.totalExperienceCount}</div>
              )}
              {formErrors.currentPositionCount && (
                <div className="small">{formErrors.currentPositionCount}</div>
              )}
              {formErrors.externalCurrentRole && (
                <div className="small">{formErrors.externalCurrentRole}</div>
              )}
            </div>
          )}
        </div>
      ),

      "leadership-roles": (
        <div className="container">
          <div className="row text-secondary fw-light">
            <div className="col-md-4">
              <div className="labelheading">
                Employee Resource Groups
                <TooltipIcon tooltipText={getTooltipById("ergs")} />
              </div>
              {isEditing ? (
                <div className="custom-multiselect-wrapper">
                  <MultiSelect
                    value={
                      data?.EmpResourceGroup?.map(
                        (rg: any) => rg.resource_group_dropdown_uid
                      ) || []
                    }
                    options={resourceGroupOptions}
                    onChange={(e) => {
                      const selectedIds = e.value;
                      // Map selected IDs to EmpResourceGroup objects, preserving PKIDs for existing entries
                      const selectedGroups = selectedIds.map((id: number) => {
                        // Find existing resource group with this id to preserve PKID
                        const existingGroup = data?.EmpResourceGroup?.find(
                          (rg: any) =>
                            rg.resource_group_dropdown_uid === id ||
                            rg.resource_group_dropdown
                              ?.resource_group_dropdown_uid === id
                        );

                        const dropdown = (dropdowns as any).resourceGroups.find(
                          (rg: any) => rg.resource_group_dropdown_uid === id
                        );

                        // If it exists in data, preserve the PKID
                        if (
                          existingGroup &&
                          existingGroup.emp_resource_group_uid
                        ) {
                          return {
                            emp_resource_group_uid:
                              existingGroup.emp_resource_group_uid,
                            resource_group_dropdown_uid: id,
                            resource_group_dropdown: dropdown,
                          };
                        } else {
                          // New selection
                          return {
                            resource_group_dropdown_uid: id,
                            resource_group_dropdown: dropdown,
                          };
                        }
                      });
                      setData({
                        ...data,
                        EmpResourceGroup: selectedGroups,
                      });
                    }}
                    optionLabel="label"
                    optionValue="value"
                    display="chip"
                    placeholder="Select Resource Groups"
                    className="w-100"
                    showClear={true}
                    resetFilterOnHide={true}
                    panelClassName="languages-multiselect-panel"
                    maxSelectedLabels={0}
                    selectedItemsLabel={getSelectedItemsLabel(
                      data?.EmpResourceGroup?.map((rg: any) => rg.resource_group_dropdown_uid) || [],
                      resourceGroupOptions,
                      'groups'
                    )}
                  />
                </div>
              ) : (
                <div className="clsValues">
                  {data?.EmpResourceGroup && data.EmpResourceGroup.length > 0
                    ? renderViewList(
                        data.EmpResourceGroup,
                        'ergs',
                        (rg: any) => rg.resource_group_dropdown?.resource_group
                      )
                    : "-"}
                </div>
              )}
            </div>
            <div className="col-md-4">
              <div className="labelheading">
                TTEC Communities
                <TooltipIcon tooltipText={getTooltipById("communities")} />
              </div>
              {isEditing ? (
                <div>
                  <input
                    className={`form-control form-control-sm ${getFieldError('ttecCommunity') ? 'is-invalid' : ''}`}
                    name="ttecCommunity"
                    value={ttecCommunity}
                    onChange={(e) => {
                      clearSavedDataWarning('ttecCommunity');
                      const validated = validateAndSanitize('ttecCommunity', e.target.value, 'ttecCommunities');
                      setTtecCommunity(validated);
                    }}
                    placeholder="E.g. TTEC Foundation"
                    maxLength={200}
                  />
                  {getFieldError('ttecCommunity') && (
                    <small className="text-danger d-block">{VALIDATION_ERROR_MESSAGE}</small>
                  )}
                </div>
              ) : (
                <div className="clsValues">{ttecCommunity || "-"}</div>
              )}
            </div>
          </div>
        </div>
      ),

      "leadership-culture": (
        <div className="container">
          <div className="row text-secondary fw-light">
            <div className="col-md-4">
              <div className="labelheading">
                Strengths Displayed
                <TooltipIcon tooltipText={getTooltipById("strengths")} />
              </div>
              {isEditing && (
                <div className="form-text mb-1">Choose Two Strengths</div>
              )}
              {isEditing ? (
                <div className="custom-multiselect-wrapper">
                  <MultiSelect
                    value={strengthsDisplayed}
                    options={leadershipCultureOptions}
                    onChange={handleStrengthsDisplayedChange}
                    optionLabel="label"
                    optionValue="value"
                    display="chip"
                    placeholder="Select Strengths"
                    className="w-100"
                    showClear={true}
                    resetFilterOnHide={true}
                    panelClassName="languages-multiselect-panel"
                    maxSelectedLabels={3}
                    selectedItemsLabel={getSelectedItemsLabel(strengthsDisplayed, leadershipCultureOptions, 'strengths')}
                  />
                </div>
              ) : (
                <div className="clsValues">
                  {Array.isArray(data?.EmpLeadershipCulture) &&
                  data.EmpLeadershipCulture.filter((lc: any) => lc.flag === "STRENGTH").length > 0
                    ? renderViewList(
                        data.EmpLeadershipCulture.filter((lc: any) => lc.flag === "STRENGTH"),
                        'strengths',
                        (lc: any) => lc.leadership_culture_dropdown_sd?.name
                      )
                    : "-"}
                </div>
              )}
            </div>
            <div className="col-md-4">
              <div className="labelheading">
                Areas of Development
                <TooltipIcon
                  tooltipText={getTooltipById("areas-of-development")}
                />
              </div>
              {isEditing && (
                <div className="form-text mb-1">
                  Choose Two Areas of Development
                </div>
              )}
              {isEditing ? (
                <div className="custom-multiselect-wrapper">
                  <MultiSelect
                    value={areasOfDevelopment}
                    options={leadershipCultureOptions}
                    onChange={handleAreasOfDevelopmentChange}
                    optionLabel="label"
                    optionValue="value"
                    display="chip"
                    placeholder="Select Areas"
                    className="w-100"
                    showClear={true}
                    resetFilterOnHide={true}
                    panelClassName="languages-multiselect-panel"
                    maxSelectedLabels={3}
                    selectedItemsLabel={getSelectedItemsLabel(areasOfDevelopment, leadershipCultureOptions, 'development')}
                  />
                </div>
              ) : (
                <div className="clsValues">
                  {Array.isArray(data?.EmpLeadershipCulture) &&
                  data.EmpLeadershipCulture.filter((lc: any) => lc.flag === "DEVELOPMENT").length > 0
                    ? renderViewList(
                        data.EmpLeadershipCulture.filter((lc: any) => lc.flag === "DEVELOPMENT"),
                        'development',
                        (lc: any) => lc.leadership_culture_dropdown_sd?.name
                      )
                    : "-"}
                </div>
              )}
            </div>
          </div>
        </div>
      ),

      "performance-summary": (
        <div>
          {/* Top Accomplishments */}
          <div className="mb-4">
            <div className="text-primary fw-bold mb-2">
              What were your top accomplishments for the previous performance
              year (personal development or business deliverable)?
            </div>
            {isEditing ? (
              <div>
                <div className="form-text mb-1">
                  Please, enter 3 to 5 accomplishments and/or deliverables for
                  the previous performance year
                </div>
                <textarea
                  className={`form-control ${getFieldHighlightClass(
                    "last_year_top_achievement"
                  )} ${getFieldError('accomplishments') ? 'is-invalid' : ''}`}
                  rows={4}
                  name="last_year_top_achievement"
                  value={accomplishments}
                  onChange={(e) => {
                    const validated = validateAndSanitize('accomplishments', e.target.value, 'performanceSummary');
                    setAccomplishments(validated);
                  }}
                  placeholder="List your accomplishments..."
                  maxLength={150}
                />
                {getFieldError('accomplishments') && (
                  <small className="text-danger d-block">{VALIDATION_ERROR_MESSAGE}</small>
                )}
                <small className="text-muted mt-1 d-block">
                  {accomplishments.length}/150 characters
                </small>
              </div>
            ) : (
              <div className="text-secondary clsValues">
                {accomplishments || "No accomplishments listed"}
              </div>
            )}
          </div>

          {/* Areas for Improvement */}
          <div className="mb-4">
            <div className="text-primary fw-bold mb-2">
              What would you have liked to have done better during the previous
              performance year (personal development or business deliverable)?
            </div>
            <div className="mb-3">
              <div className="text-primary fw-bold mb-2">
                Provide an example where you didn't achieve the result you were
                hoping for. How did you overcome it/ what did you learn from the
                experience?
              </div>
              {isEditing ? (
                <div>
                  <textarea
                    className={`form-control ${getFieldHighlightClass(
                      "last_year_personal_development_review"
                    )} ${getFieldError('redo') ? 'is-invalid' : ''}`}
                    rows={4}
                    name="last_year_personal_development_review"
                    value={redo}
                    onChange={(e) => {
                      const validated = validateAndSanitize('redo', e.target.value, 'performanceSummary');
                      setRedo(validated);
                    }}
                    placeholder="Describe what you would have done better..."
                    maxLength={150}
                  />
                  {getFieldError('redo') && (
                    <small className="text-danger d-block">{VALIDATION_ERROR_MESSAGE}</small>
                  )}
                  <small className="text-muted mt-1 d-block">
                    {redo.length}/150 characters
                  </small>
                </div>
              ) : (
                <div className="text-secondary clsValues">
                  {redo || "No improvements listed"}
                </div>
              )}
            </div>

            <div className="mb-3">
              <div className="text-primary fw-bold mb-2">
                If you had a redo for a prior business deliverable, what would
                it be?
              </div>
              {isEditing ? (
                <div>
                  <textarea
                    className={`form-control ${getFieldHighlightClass(
                      "redo_for_previous_business_year"
                    )} ${getFieldError('redoBusiness') ? 'is-invalid' : ''}`}
                    rows={4}
                    name="redo_for_previous_business_year"
                    value={redoBusiness || ""}
                    onChange={(e) => {
                      const validated = validateAndSanitize('redoBusiness', e.target.value, 'performanceSummary');
                      setRedoBusiness(validated);
                      handleChange(e);
                    }}
                    placeholder="Describe your redo for a prior business deliverable..."
                    maxLength={150}
                  />
                  {getFieldError('redoBusiness') && (
                    <small className="text-danger d-block">{VALIDATION_ERROR_MESSAGE}</small>
                  )}
                  <small className="text-muted mt-1 d-block">
                    {redoBusiness.length}/150 characters
                  </small>
                </div>
              ) : (
                <div className="text-secondary clsValues">
                  {redoBusiness || "No business redo listed"}
                </div>
              )}
            </div>
          </div>

          {/* Goals & Focus Areas */}
          <div className="mb-3">
            <div className="text-primary fw-bold mb-2">
              What are your goals or focus areas for the next two quarters? What
              areas of development are you focused on and your major milestones
              ahead of you?
            </div>
            {isEditing ? (
              <div>
                <textarea
                  className={`form-control ${getFieldHighlightClass(
                    "goal_next_two_quarters"
                  )} ${getFieldError('goals') ? 'is-invalid' : ''}`}
                  rows={4}
                  name="goal_next_two_quarters"
                  value={goals}
                  onChange={(e) => {
                    const validated = validateAndSanitize('goals', e.target.value, 'performanceSummary');
                    setGoals(validated);
                  }}
                  placeholder="Describe your goals or focus areas..."
                  maxLength={150}
                />
                {getFieldError('goals') && (
                  <small className="text-danger d-block">{VALIDATION_ERROR_MESSAGE}</small>
                )}
                <small className="text-muted mt-1 d-block">
                  {goals.length}/150 characters
                </small>
              </div>
            ) : (
              <div className="text-secondary clsValues">
                {goals || "No goals listed"}
              </div>
            )}
          </div>
        </div>
      ),
    };

    return contentMap[sectionId] || <div>Content not available</div>;
  };

  return (
    <form>
      {isLoading && <LoadingProgress />}
      <div style={{ position: "relative" }}>
        {pdfProgress.show && (
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "rgba(255, 255, 255, 0.98)",
              color: "#333333",
              padding: "30px 40px",
              borderRadius: "12px",
              border: "2px solid rgb(244, 247, 249)",
              boxShadow: "0 15px 35px rgba(0, 0, 0, 0.3)",
              zIndex: 9999,
              textAlign: "center",
              fontSize: "18px",
              fontWeight: "600",
              minWidth: "200px",
            }}
          >
            <div>Generating PDF...</div>
            <div>
              Processing {pdfProgress.current} of {pdfProgress.total}
            </div>
            <div
              style={{
                width: "200px",
                height: "8px",
                backgroundColor: "#333",
                borderRadius: "4px",
                margin: "10px 0",
              }}
            >
              <div
                style={{
                  width: `${(pdfProgress.current / pdfProgress.total) * 100}%`,
                  height: "100%",
                  backgroundColor: "#4CAF50",
                  borderRadius: "4px",
                  transition: "width 0.3s ease",
                }}
              ></div>
            </div>
          </div>
        )}

        {/* Full-screen blocking spinner when API is loading */}
        {(empDetails.status === "loading" ||
          addCareerCardStatus === "loading" ||
          addCareerCardOtherDetailsStatus === "loading" ||
          empOracleDetails.status === "loading" ||
          importAPIState === "loading") && <LoadingProgress />}

        {/* Cancel Confirmation Modal */}
        {showCancelConfirm && (
          <Modal show={true} backdrop="static" keyboard={false} centered>
            <Modal.Header>
              <Modal.Title>Confirm Cancel</Modal.Title>
              <Button
                variant="close"
                onClick={() => setShowCancelConfirm(false)}
              ></Button>
            </Modal.Header>
            <Modal.Body>
              <p>
                Are you sure you want to cancel? All unsaved changes will be
                lost.
              </p>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowCancelConfirm(false)}
              >
                Keep Editing
              </Button>
              <Button variant="danger" onClick={confirmCancel}>
                Yes, Cancel Changes
              </Button>
            </Modal.Footer>
          </Modal>
        )}
        {showDeleteExperienceConfirm && (
          <Modal show={true} backdrop="static" keyboard={false} centered>
            <Modal.Header>
              <Modal.Title>
                Confirm Delete Experience
              </Modal.Title>
              <Button
                variant="close"
                onClick={cancelDeleteExperience}
              ></Button>
            </Modal.Header>
            <Modal.Body>
              <p>
                Are you sure you want to delete this experience entry?
              </p>
              <p className="text-danger fw-bold mb-0">
                <i className="bi bi-exclamation-circle me-1"></i>
                This action cannot be undone.
              </p>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={cancelDeleteExperience}
              >
                Cancel
              </Button>
              <Button variant="danger" onClick={confirmDeleteExperience}>
                Yes, Delete Experience
              </Button>
            </Modal.Footer>
          </Modal>
        )}

        <div className="rounded-4 career-card">
          <div className="card p-4 shadow">
            {/* Show form errors at the top */}
            {Object.keys(formErrors).length > 0 && (
              <div
                ref={errorContainerRef}
                className="alert alert-danger"
                tabIndex={-1}
                style={{
                  boxShadow: "0 0 10px rgba(220, 53, 69, 0.5)",
                  border: "1px solid #dc3545",
                  borderRadius: "8px",
                  padding: "15px 20px",
                  marginBottom: "20px",
                }}
              >
                <h5 className="alert-heading">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  Please fix the following errors:
                </h5>
                <ul className="mb-0">
                  {Object.entries(formErrors).map(([key, msg]) => (
                    <li key={key}>{msg as string}</li>
                  ))}
                </ul>
              </div>
            )}

            {showImportErrorBanner && (
              <div
                className="alert alert-danger alert-dismissible"
                role="alert"
                style={{
                  borderRadius: "8px",
                  padding: "15px 20px",
                  marginBottom: "20px",
                  border: "1px solid #dc3545",
                }}
              >
                <div className="d-flex align-items-center mb-2">
                  <i className="bi bi-x-circle-fill me-2 text-danger"></i>
                  <strong>Excel Import Failed — Validation Errors</strong>
                  <button
                    type="button"
                    className="btn-close ms-auto"
                    onClick={() => setShowImportErrorBanner(false)}
                    aria-label="Close"
                  ></button>
                </div>
                {importErrorMessage && (
                  <p className="mb-2" style={{ fontSize: '0.9em' }}>
                    {importErrorMessage}
                  </p>
                )}
                {importValidationErrors.length > 0 && (
                  <div>
                    <p className="mb-1" style={{ fontSize: '0.9em' }}>
                      The following fields contain blocked special characters. Please correct them in the Excel file and re-import:
                    </p>
                    <ul className="mb-0" style={{ fontSize: '0.9em' }}>
                      {importValidationErrors.map((err, idx) => (
                        <li key={idx}>
                          <strong>{err.field}</strong>
                          {err.blockedChars && err.blockedChars.length > 0 && (
                            <span className="text-danger ms-1">
                              — found: <code style={{ color: '#dc3545', backgroundColor: '#f8d7da', padding: '1px 4px', borderRadius: '3px' }}>
                                {err.blockedChars.join('  ')}
                              </code>
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {showSavedDataWarningBanner && savedDataWarnings.length > 0 && (
              <div
                className="alert alert-warning alert-dismissible"
                role="alert"
                style={{
                  borderRadius: "8px",
                  padding: "15px 20px",
                  marginBottom: "20px",
                  border: "1px solid #ffc107",
                }}
              >
                <div className="d-flex align-items-center mb-2">
                  <i className="bi bi-exclamation-triangle-fill me-2 text-warning"></i>
                  <strong>Special Characters Detected in Saved Data</strong>
                  <span className="ms-2 badge bg-warning text-dark" style={{ fontSize: '0.8em' }}>
                    {savedDataWarnings.length} field{savedDataWarnings.length > 1 ? 's' : ''}
                  </span>
                  <button
                    type="button"
                    className="btn-close ms-auto"
                    onClick={() => setShowSavedDataWarningBanner(false)}
                    aria-label="Close"
                  ></button>
                </div>
                <p className="mb-2" style={{ fontSize: '0.9em' }}>
                  The following fields contain blocked special characters. Start updating information and click <strong>"Fix Next"</strong> to navigate to each field and remove them:
                </p>
                <div className="d-flex align-items-center mb-2 p-2" style={{
                  backgroundColor: '#fff3cd',
                  borderRadius: '6px',
                  border: '1px solid #ffecb5',
                }}>
                  <span style={{ fontSize: '0.9em', flex: 1 }}>
                    <strong>{currentWarningIndex + 1}/{savedDataWarnings.length}</strong>
                    {' — '}
                    <span
                      style={{ cursor: 'pointer', textDecoration: 'underline', color: '#664d03' }}
                      onClick={() => scrollToField(savedDataWarnings[currentWarningIndex].field)}
                    >
                      {savedDataWarnings[currentWarningIndex].displayName}
                    </span>
                    {savedDataWarnings[currentWarningIndex].blockedChars.length > 0 && (
                      <span className="ms-1">
                        — found: <code style={{ color: '#856404', backgroundColor: '#ffe69c', padding: '1px 4px', borderRadius: '3px' }}>
                          {savedDataWarnings[currentWarningIndex].blockedChars.join('  ')}
                        </code>
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-warning ms-2 fw-bold"
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={() => {
                      scrollToField(savedDataWarnings[currentWarningIndex].field);
                      if (savedDataWarnings.length > 1) {
                        setCurrentWarningIndex(prev =>
                          prev < savedDataWarnings.length - 1 ? prev + 1 : 0
                        );
                      }
                    }}
                  >
                    <i className="bi bi-arrow-right-circle me-1"></i>
                    {savedDataWarnings.length === 1 ? 'Go to Field' : 'Fix Next'}
                  </button>
                </div>
                {savedDataWarnings.length > 1 && (
                  <ul className="mb-0 mt-2" style={{ fontSize: '0.85em', opacity: 0.85 }}>
                    {savedDataWarnings.map((warning, idx) => (
                      <li key={idx}
                        style={{
                          cursor: 'pointer',
                          fontWeight: idx === currentWarningIndex ? 'bold' : 'normal',
                        }}
                        onClick={() => {
                          setCurrentWarningIndex(idx);
                          scrollToField(warning.field);
                        }}
                        title={`Click to go to ${warning.displayName}`}
                      >
                        <span style={{ textDecoration: 'underline' }}>{warning.displayName}</span>
                        {warning.blockedChars.length > 0 && (
                          <span className="ms-1">
                            — <code style={{ color: '#856404', padding: '1px 3px' }}>
                              {warning.blockedChars.join('  ')}
                            </code>
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Action buttons */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              {!isView && (
                <h5 className="text-primary fw-bold mb-0">Career Card</h5>
              )}
              {!isEditing && !isView && (
                <div className="d-flex gap-2">
                  {/* {data?.oracle_id === localStorage.getItem("cc-oracleId") ? ( */}
                  <button
                    type="button"
                    className="btn btn-primary d-flex align-items-center gap-2 updateInfo"
                    onClick={handleUpdateClick}
                  >
                    <i className="bi bi-pencil"></i>
                    Update Information
                  </button>
                  {/*} ) : (
                  //   <button
                  //     type="button"
                  //     className="btn btn-primary d-flex align-items-center gap-2 updateInfo"
                  //     onClick={handleGoBackToYourProfileClick}
                  //   >
                  //     <i className="bi bi-arrow-repeat"></i>
                  //     Go back to your profile
                  //   </button>
                  // )
                  // } */}

                  <Dropdown>
                    <Dropdown.Toggle
                      variant="primary"
                      className="d-flex align-items-center gap-2 updateInfo"
                    >
                      <i className="bi bi-arrow-down-up" aria-hidden="true"></i>
                      Import/Export
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      {/* {data?.oracle_id ===
                        localStorage.getItem("cc-oracleId") ? ( */}
                      <Dropdown.Item
                        onClick={handleImportFromExcel}
                        title={getTooltipById("import-excel")}
                      >
                        <i className="pi pi-download me-1" aria-hidden="true"></i> Import from Excel
                      </Dropdown.Item>
                      {/* ) : (
                        ""
                      )} */}

                      <Dropdown.Item
                        onClick={handleExportToExcel}
                        title={getTooltipById("export-options")}
                      >
                        <i className="pi pi-upload me-1" aria-hidden="true"></i> Export to Excel
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={handleExportToPDF}
                        title={getTooltipById("export-options")}
                      >
                        <i className="pi pi-upload me-1" aria-hidden="true"></i> Export to PDF
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>

                  {/* Hidden file input */}
                  <input
                    type="file"
                    accept=".xlsx,.xls,.zip"
                    style={{ display: "none" }}
                    ref={importExcelFileInputRef}
                    onChange={handleFileChange}
                  />
                </div>
              )}
            </div>

            {/* Changed fields highlight banner */}
            {showChangedFieldsIndicator && changedFields.size > 0 && (
              <div
                className="alert alert-warning alert-dismissible fade show mb-3"
                role="alert"
              >
                <div className="d-flex align-items-center">
                  <i className="bi bi-info-circle-fill me-2"></i>
                  <div>
                    <strong>Excel Import Changes Detected:</strong>{" "}
                    {changedFields.size} field
                    {changedFields.size > 1 ? "s" : ""} have been updated and
                    are highlighted in yellow.
                    <small className="d-block mt-1 text-muted">
                      Changed fields:{" "}
                      {Array.from(changedFields)
                        .map((field) =>
                          field
                            .replace(/([A-Z])/g, " $1")
                            .replace(/^./, (str) => str.toUpperCase())
                            .replace(/_/g, " ")
                        )
                        .join(", ")}
                    </small>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => {
                    setShowChangedFieldsIndicator(false);
                    setChangedFields(new Set());
                  }}
                ></button>
              </div>
            )}

            {/* Section Controls */}
            <div className="section-controls mb-4 d-flex justify-content-between align-items-center bg-light p-3 rounded-3 shadow-sm">
              <div className="d-flex align-items-center">
                <i
                  className="pi pi-list section-icon me-2"
                  style={{ fontSize: "1.2rem" }}
                  aria-hidden="true"
                ></i>
                <span className="fw-bold text-dark">Career Card Sections</span>
                <span className="badge bg-primary ms-2">
                  {expandedSections.size} of {CAREER_CARD_SECTIONS.length}{" "}
                  expanded
                </span>
              </div>
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={expandAllSections}
                  title="Expand All Sections"
                >
                  <i className="pi pi-angle-double-up me-1" aria-hidden="true"></i>
                  Expand All
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={collapseAllSections}
                  title="Collapse All Sections"
                >
                  <i className="pi pi-angle-double-down me-1" aria-hidden="true"></i>
                  Collapse All
                </button>
              </div>
            </div>

            {/* Accordion Sections */}
            <div className="sections-container">
              {CAREER_CARD_SECTIONS.map((section) => {
                const isExpanded = expandedSections.has(section.id);
                return (
                  <div
                    key={section.id}
                    className={`section-card mb-3 border-0 shadow-sm rounded-3 overflow-hidden ${
                      isExpanded ? "expanded" : ""
                    }`}
                  >
                    <div
                      className={`section-header cursor-pointer user-select-none ${
                        isExpanded ? "active" : ""
                      }`}
                      onClick={() => toggleSection(section.id)}
                      title={section.tooltip}
                      style={{
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        background: isExpanded
                          ? "linear-gradient(135deg, #007bff 0%, #0056b3 100%)"
                          : "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center py-2 px-3" style={{ minHeight: "52px" }}>
                        <div className="d-flex align-items-center">
                          <div
                            className="section-icon-wrapper me-2 rounded-circle"
                            style={{
                              backgroundColor: isExpanded
                                ? "rgba(255, 255, 255, 0.25)"
                                : "rgba(0, 123, 255, 0.1)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "32px",
                              height: "32px",
                            }}
                          >
                            <i
                              className={section.icon}
                              style={{
                                fontSize: "0.95rem",
                                color: isExpanded ? "#ffffff" : "#007bff",
                              }}
                              aria-hidden="true"
                            ></i>
                          </div>
                          <div>
                            <h6
                              className={`mb-0 fw-semibold ${
                                isExpanded ? "text-white" : "text-dark"
                              }`}
                              style={{ fontSize: "0.95rem" }}
                            >
                              {section.title}
                            </h6>
                            <small
                              className={`${
                                isExpanded
                                  ? "text-white text-opacity-75"
                                  : "text-muted"
                              }`}
                              style={{ fontSize: "0.75rem" }}
                            >
                              {section.description}
                            </small>
                          </div>
                        </div>
                        <div className="d-flex align-items-center">
                          <span
                            className={`badge me-2 ${
                              isExpanded
                                ? "bg-white text-primary"
                                : "bg-primary text-white"
                            }`}
                            style={{ fontSize: "0.7rem", padding: "4px 8px" }}
                          >
                            {isExpanded ? "Expanded" : "Collapsed"}
                          </span>
                          <i
                            className={`pi pi-chevron-down ${
                              isExpanded ? "text-white" : "text-primary"
                            }`}
                            style={{
                              fontSize: "0.85rem",
                              transition: "transform 0.3s ease",
                              transform: isExpanded
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                            }}
                            aria-hidden="true"
                          ></i>
                        </div>
                      </div>
                    </div>

                    <div
                      className={`section-body ${isExpanded ? "show" : ""}`}
                      style={{
                        maxHeight: isExpanded ? "2000px" : "0",
                        overflow: "hidden",
                        transition: "all 0.5s ease",
                      }}
                    >
                      <div className="section-content-wrapper p-4 bg-white border-top">
                        {renderSectionContent(section.id)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Save/Cancel Buttons at the bottom when editing */}
            {isEditing && (
              <>
                {!isAdminView && (
                  <div
                    className={`container-email ${
                      showEmailModal ? "error-border" : ""
                    }`}
                  >
                    <label className="checkbox">
                      <input
                        type="checkbox"
                        checked={emailMe}
                        onChange={() => setEmailMe(!emailMe)}
                      />
                      {"  "}Send Email to me
                    </label>
                    <label className="checkbox">
                      <input
                        type="checkbox"
                        checked={emailManager}
                        onChange={() => setEmailManager(!emailManager)}
                      />
                      {"  "}Send Email to my manager
                    </label>
                    <p className="note">
                      Note: Emails will be sent only if one or more checkboxes
                      above are selected.
                    </p>
                    {showEmailModal && (
                      <Modal
                        show={showEmailModal}
                        onHide={() => setShowEmailModal(false)}
                        backdrop="static"
                        keyboard={false}
                        centered
                      >
                        <Modal.Header closeButton>
                          <Modal.Title>Email Confirmation</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                          {(() => {
                            if (!emailMe && !emailManager) {
                              return (
                                <p>
                                  Please select the checkboxes below the form to
                                  opt for email notifications with pdf to self
                                  and your manager.
                                </p>
                              );
                            } else if (!emailMe) {
                              return (
                                <p>
                                  Please select the checkbox below the form to
                                  opt for email notification with pdf to self.
                                </p>
                              );
                            } else if (!emailManager) {
                              return (
                                <p>
                                  Please select the checkbox below the form to
                                  opt for email notification with pdf to your
                                  manager.
                                </p>
                              );
                            }
                            return null;
                          })()}
                          <p>
                            Please click on 'continue' to proceed or please
                            click 'cancel' to change mail notification options.
                          </p>
                        </Modal.Body>
                        <Modal.Footer>
                          <Button
                            variant="secondary"
                            onClick={() => setShowEmailModal(false)}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="primary"
                            onClick={() => {
                              setShowEmailModal(false);
                              handleSaveCareerCard(null, true);
                            }}
                          >
                            Continue
                          </Button>
                        </Modal.Footer>
                      </Modal>
                    )}
                  </div>
                )}
                <div className="d-flex justify-content-end gap-2 mt-4">
                  <button
                    className="btn btn-primary px-4"
                    type="button"
                    onClick={() => {
                      handleSaveCareerCard(null, false);
                    }}
                    //disabled={isLoading}
                  >
                    {isLoading ? "Saving..." : "Save"}
                  </button>
                  <button
                    className="btn btn-secondary px-4"
                    onClick={handleCancel}
                    type="button"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Hidden background container - only used during PDF generation */}
        <div
          ref={backgroundPdfRef}
          style={{
            position: "absolute",
            left: "-9999px",
            top: "-9999px",
            width: "850px",
            fontSize: "16px",
            lineHeight: "1.6",
            color: "#000",
            backgroundColor: "#fff",
            padding: "20px",
            visibility: "hidden",
          }}
        />

        {/* Success modal */}
        {/* <Modal show={showSuccessModal} centered>
          <Modal.Header>
            <Modal.Title>Success</Modal.Title>
          </Modal.Header>
          <Modal.Body>Successfully submitted!</Modal.Body>
          <Modal.Footer>
            <Button
              variant="primary"
              onClick={() => setShowSuccessModal(false)}
            >
              OK
            </Button>
          </Modal.Footer>
        </Modal> */}

        {/* Export success modal */}
        <Modal show={showExportSuccess} centered>
          <Modal.Header>
            <Modal.Title>Export Successful</Modal.Title>
            <Button
              variant="close"
              onClick={() => setShowExportSuccess(false)}
            ></Button>
          </Modal.Header>
          <Modal.Body>
            Your career card data has been successfully exported to Excel!
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="primary"
              onClick={() => setShowExportSuccess(false)}
            >
              OK
            </Button>
          </Modal.Footer>
        </Modal>
        <div
          className="toast-container position-fixed top-0 end-0 p-3"
          style={{ zIndex: 10001 }}
        >
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              onClose={() => removeToast(toast.id)}
              showProgress={toast.showProgress}
              duration={toast.duration}
            />
          ))}
        </div>
      </div>
    </form>
  );
};

export default CareerCard;
